import _ from "lodash";
import moment from "moment";
import { HOSPITAL_CARDS_LABELS, HOSPITAL_CARDS_TYPE } from "../types/hospital.type";
import {
    insuranceData,
    floorsData,
    DCERDataFilter,
    hospitalizationsFilter,
    returnsDataFilter,
    ninetyDaysDataFilter,
    dcErData,
    hospitalizationData,
    returnsData,
    ninetyDaysDataList,
    daysData,
    dxData,
    nurseData,
    doctorData,
    shiftData,
    customHospitalData
} from "./hospital-common";
import { filterCustomPatientData, itemPercentage, processDynamicCard } from "./common";
import { CO_TRANSFER_CARDS_TYPE, COMMUNITY_CARD_LABELS } from "../types/community-transfer.type";
import { safeDischargeAssLivData, sixtyDaysDataList, snfFacilityData } from "./community-common";
import { CUSTOM_TAB_PAGE_TYPE, PAGE_TYPE } from "../types/pages.type";
import { ADT_SUB_TYPES, ADT_TYPES, CUSTOM_TAB_TYPE } from "../types/common.type";
import { DECEASED_CARDS_LABELS, DECEASED_CARDS_TYPE } from "../types/deceased.type";
import { ADMISSION_CARDS_LABELS, ADMISSION_CARDS_TYPE } from "../types/admission.type";
import { OVERALL_CARDS_TYPE } from "../types/overall.type";

// Constants for better maintainability
const TRANSFER_TYPES = [
    CO_TRANSFER_CARDS_TYPE.AMA,
    CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE,
    CO_TRANSFER_CARDS_TYPE.SNF,
    HOSPITAL_CARDS_TYPE.UNPLANNED,
    HOSPITAL_CARDS_TYPE.PLANNED
];

const ADMISSION_TYPES = [
    ADMISSION_CARDS_TYPE.ADMISSION,
    ADMISSION_CARDS_TYPE.READMISSION
];

const SIMPLE_CARD_KEYS = [
    HOSPITAL_CARDS_TYPE.UNPLANNED,
    HOSPITAL_CARDS_TYPE.PLANNED,
    HOSPITAL_CARDS_TYPE.TOTAL,
    CO_TRANSFER_CARDS_TYPE.SNF,
    CO_TRANSFER_CARDS_TYPE.AMA,
    CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE,
    CO_TRANSFER_CARDS_TYPE.TOTAL,
    DECEASED_CARDS_TYPE.TOTAL,
    ADMISSION_CARDS_TYPE.ADMISSION,
    ADMISSION_CARDS_TYPE.READMISSION,
    ADMISSION_CARDS_TYPE.TOTAL
];

// Categorize dashboards for "Clean Chain" logic
const INCOMING_DASHBOARDS = [PAGE_TYPE.ADMISSION, PAGE_TYPE.READMISSION];
const OUTGOING_DASHBOARDS = [PAGE_TYPE.HOSPITAL, PAGE_TYPE.COMMUNITY_TRANSFER, PAGE_TYPE.DECEASED];

/**
 * Verifies if the transition between two events is "direct" (no intermediate events of the same category).
 * @param {Object} targetEvent - The event at level N
 * @param {Object} refEvent - The reference event (level 0)
 * @param {Object} diffDashboardPatients - All dashboard data
 * @param {string} targetDashboard - The dashboard of the target event
 * @returns {boolean}
 */
/**
 * Level for each custom-tab data point (filter index).
 * Matches the level assignment in getCustomTabsCards.
 */
function calculateCustomTabFilterLevel(filterIndex, customTab, pageType) {
    const focusDataPointIndex = customTab?.focusDataPointIndex ?? null;
    const focusIndex = focusDataPointIndex !== null && focusDataPointIndex !== undefined
        ? Number(focusDataPointIndex)
        : null;
    const selectedDataPoints = customTab.filters.map(f => f.dashboard);
    const hasSameTypeOnPage = selectedDataPoints.filter(d => d === pageType).length > 1;

    if (hasSameTypeOnPage && focusIndex !== null && !Number.isNaN(focusIndex)) {
        if (filterIndex < focusIndex) {
            let pastLevel = 0;
            const currentDashboard = customTab.filters[filterIndex].dashboard;
            for (let i = 0; i < filterIndex; i++) {
                if (customTab.filters[i].dashboard === currentDashboard) {
                    pastLevel++;
                }
            }
            return -(pastLevel + 1);
        }
        if (filterIndex === focusIndex) {
            return 0;
        }
        let futureLevel = 0;
        const currentDashboard = customTab.filters[filterIndex].dashboard;
        for (let i = focusIndex + 1; i < filterIndex; i++) {
            if (customTab.filters[i].dashboard === currentDashboard) {
                futureLevel++;
            }
        }
        return futureLevel + 1;
    }

    const matchingIndex = customTab.filters.findIndex(f => f.dashboard === pageType);
    if (matchingIndex === -1) {
        return 0;
    }

    if (filterIndex < matchingIndex) {
        let pastLevel = 0;
        const currentDashboard = customTab.filters[filterIndex].dashboard;
        for (let i = 0; i < filterIndex; i++) {
            if (customTab.filters[i].dashboard === currentDashboard) {
                pastLevel++;
            }
        }
        return -(pastLevel + 1);
    }
    if (filterIndex === matchingIndex) {
        return 0;
    }
    let futureLevel = 0;
    const currentDashboard = customTab.filters[filterIndex].dashboard;
    for (let i = matchingIndex + 1; i < filterIndex; i++) {
        if (customTab.filters[i].dashboard === currentDashboard) {
            futureLevel++;
        }
    }
    return futureLevel + 1;
}

/**
 * Event ids for other custom-tab data points on the same dashboard only (sibling steps).
 * Does NOT exclude unrelated same-category events (e.g. Readmission between two Admissions,
 * or Community between two Hospitals) — those still break the chain per clean-chain rules.
 */
function getCleanChainExcludeIds(diffDashboardPatients, customTab, currentFilterIndex, filterLevels) {
    const exclude = new Set();
    const currentLevel = filterLevels[currentFilterIndex];
    const currentDashboard = customTab.filters[currentFilterIndex]?.dashboard;

    if (!currentLevel || currentLevel === 0 || !currentDashboard) {
        return exclude;
    }

    customTab.filters.forEach((filter, filterIndex) => {
        if (filterIndex === currentFilterIndex || filter.dashboard !== currentDashboard) {
            return;
        }

        const otherTabLevel = filterLevels[filterIndex];
        const isSiblingStepBetweenCurrentAndAnchor =
            (currentLevel < 0 && otherTabLevel < 0 && otherTabLevel > currentLevel) ||
            (currentLevel > 0 && otherTabLevel > 0 && otherTabLevel < currentLevel);

        if (!isSiblingStepBetweenCurrentAndAnchor) {
            return;
        }

        const list = diffDashboardPatients[currentDashboard]?.list ?? [];
        for (const item of list) {
            if (item.level !== otherTabLevel) {
                continue;
            }
            if (currentLevel < 0 && item.isPastData !== true) {
                continue;
            }
            if (currentLevel > 0 && item.isFutureData !== true) {
                continue;
            }
            const eventId = (item._id || item.id)?.toString();
            if (eventId) {
                exclude.add(eventId);
            }
        }
    });

    return exclude;
}

/**
 * Direct transition check (incoming↔incoming or outgoing↔outgoing).
 * @param {Set<string>} excludeEventIds - Only sibling custom-tab datapoints on the same dashboard.
 */
function isCleanChain(targetEvent, refEvent, diffDashboardPatients, targetDashboard, excludeEventIds = new Set()) {
    if (!targetEvent?.dateOfADT || !refEvent?.dateOfADT) return true;

    const isIncoming = INCOMING_DASHBOARDS.includes(targetDashboard);
    const isOutgoing = OUTGOING_DASHBOARDS.includes(targetDashboard);

    // If it's not an incoming or outgoing dashboard we track for clean chains, return true
    if (!isIncoming && !isOutgoing) return true;

    const dashboardsToCheck = isIncoming ? INCOMING_DASHBOARDS : OUTGOING_DASHBOARDS;

    // Use standard moment and startOf('day') for consistent comparisons of date strings
    const targetDate = moment(targetEvent.dateOfADT).startOf('day');
    const refDate = moment(refEvent.dateOfADT).startOf('day');

    const start = moment.min(targetDate, refDate);
    const end = moment.max(targetDate, refDate);

    for (const dbKey of dashboardsToCheck) {
        if (!dbKey) continue;
        const events = diffDashboardPatients[dbKey]?.list || [];
        // Check if ANY event for this resident exists strictly between the two dates
        const intermediate = events.find(e => {
            const eventId = (e._id || e.id)?.toString();
            return e.refPatientId === targetEvent.refPatientId &&
                eventId !== (targetEvent._id || targetEvent.id)?.toString() &&
                eventId !== (refEvent._id || refEvent.id)?.toString() &&
                !excludeEventIds.has(eventId) &&
                e.dateOfADT &&
                moment(e.dateOfADT).startOf('day').isAfter(start) &&
                moment(e.dateOfADT).startOf('day').isBefore(end);
        });
        if (intermediate) return false;
    }
    return true;
}

/**
 * Matches the specific subtype of an ADT event (e.g. Admission vs Readmission) 
 * for simple cards that represent specific subtypes.
 */
function matchesSubtype(item, key) {
    if (TRANSFER_TYPES.includes(key)) {
        return item.type === "transfer" && item.transferType === key;
    }
    if (ADMISSION_TYPES.includes(key)) {
        return item.type === key;
    }
    return true;
}

// Input validation helper
function validateFilterInputs(patientData, customTab) {
    if (!Array.isArray(patientData)) {
        throw new Error('patientData must be an array');
    }

    if (!customTab || typeof customTab !== 'object') {
        throw new Error('customTab is required and must be an object');
    }

    if (!Array.isArray(customTab.filters)) {
        throw new Error('customTab.filters must be an array');
    }
}

// Optimized simple card key checker using Set for O(1) lookup
const simpleCardKeysSet = new Set(SIMPLE_CARD_KEYS);
function isSimpleCardKey(key) {
    return simpleCardKeysSet.has(key);
}

export async function totalSafeDischarge(data, type) {
    const totalCount = data.filter(
        d => d.type === "transfer" && d.transferType === type
    )?.length;
    return totalCount || 0;
}

/**
 * Generate a label from date range if label doesn't exist
 * @param {Object} range - Date range object with minDays and maxDays
 * @returns {string} - Generated label
 */
function getDateRangeLabelFromRange(range) {
    if (range?.label) {
        return range.label;
    }
    const min = Number(range?.minDays ?? 0);
    const max = Number(range?.maxDays ?? (range?.minDays ?? 0));
    if (min === max) {
        return `${min} days`;
    }
    return `${min}-${max} days`;
}

/**
 * Get the date range label for a patient based on countDay
 * @param {Object} patient - Patient data object
 * @param {Array} customDateRanges - Array of date range objects with minDays, maxDays, and label
 * @returns {string} - The label of the range the patient belongs to
 */
function getDateRangeLabel(patient, customDateRanges) {
    const countDay = Number(patient?.countDay);
    if (Number.isNaN(countDay)) {
        return getDateRangeLabelFromRange(customDateRanges?.[0]);
    }

    for (const range of customDateRanges) {
        const min = Number(range?.minDays ?? 0);
        const max = Number(range?.maxDays ?? (range?.minDays ?? 0));
        if (countDay >= min && countDay <= max) {
            return getDateRangeLabelFromRange(range);
        }
    }

    return getDateRangeLabelFromRange(customDateRanges?.[0]);
}

/**
 * Generate a label from time range if label doesn't exist
 * @param {Object} range - Time range object with startTime and endTime
 * @returns {string} - Generated label
 */
function getTimeRangeLabelFromRange(range) {
    if (range?.label) {
        return range.label;
    }
    const startTime = range?.startTime || "";
    const endTime = range?.endTime || "";
    if (startTime && endTime) {
        return `${startTime} - ${endTime}`;
    }
    return startTime || endTime || "Time Range";
}

/**
 * Get the time range label for a patient based on transferTime
 * @param {Object} patient - Patient data object
 * @param {Array} customDateRanges - Array of time range objects with startTime, endTime, and label
 * @returns {string} - The label of the range the patient belongs to
 */
function getTimeRangeLabel(patient, customDateRanges) {
    const transferTime = patient?.transferTime;
    if (!transferTime) {
        return getTimeRangeLabelFromRange(customDateRanges?.[0]);
    }

    const patientTimeMinutes = normalizeTimeToMinutes(transferTime);
    if (Number.isNaN(patientTimeMinutes)) {
        return getTimeRangeLabelFromRange(customDateRanges?.[0]);
    }

    for (const range of customDateRanges) {
        const startTime = range?.startTime;
        const endTime = range?.endTime;

        if (!startTime || !endTime) continue;

        const startMinutes = normalizeTimeToMinutes(startTime);
        const endMinutes = normalizeTimeToMinutes(endTime);

        if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes)) continue;

        const spansMidnight = endMinutes < startMinutes;

        let matches = false;
        if (spansMidnight) {
            // Time range spans midnight
            matches = patientTimeMinutes >= startMinutes || patientTimeMinutes <= endMinutes;
        } else {
            // Normal time range
            matches = patientTimeMinutes >= startMinutes && patientTimeMinutes < endMinutes;
        }

        if (matches) {
            return getTimeRangeLabelFromRange(range);
        }
    }

    return getTimeRangeLabelFromRange(customDateRanges?.[0]);
}

/**
 * Build custom data array for date analysis
 * @param {Array} patientData - Array of patient data objects
 * @param {Array} customDateRanges - Array of date range objects
 * @param {number} percentageTotal - Total for percentage calculation
 * @returns {Array} - Array of data objects similar to shiftDataDynamic
 */
function buildCustomDateDataArray(patientData, customDateRanges, percentageTotal, pageType, preCalculatedAverage = null) {
    // Filter out averages from ranges for processing
    const rangesOnly = customDateRanges.filter(item => item.type !== "average");

    // Add calculatedRange to each patient (only for ranges, not averages)
    const patientDataWithRange = patientData.map(item => ({
        ...item,
        calculatedRange: getDateRangeLabel(item, rangesOnly)
    }));

    // Group by range
    const rangeDBData = _.countBy(patientDataWithRange, "calculatedRange");
    const patientIdsByRange = _.groupBy(patientDataWithRange, "calculatedRange");

    // Calculate average from range totals (sum of all range totals / number of ranges)
    // OR use pre-calculated average if provided (for NINETY_DAYS_DATA)
    const rangeTotals = rangesOnly.map(range => {
        const rangeLabel = getDateRangeLabelFromRange(range);
        return rangeDBData?.[rangeLabel] || 0;
    });

    const sumOfRangeTotals = rangeTotals.reduce((sum, total) => sum + total, 0);
    // Use pre-calculated average if provided (for NINETY_DAYS_DATA), otherwise calculate from range totals
    const averageValue = preCalculatedAverage !== null ? preCalculatedAverage : (rangesOnly.length > 0 ? sumOfRangeTotals / rangesOnly.length : 0);
    const formatValue = (value) => value < 1 ? value.toFixed(2) : Math.round(value);

    // Collect all patient IDs from all ranges for average
    const allRangePatientIds = [];
    rangesOnly.forEach(range => {
        const rangeLabel = getDateRangeLabelFromRange(range);
        const patientsInRange = patientIdsByRange?.[rangeLabel] || [];
        const patientIds = patientsInRange.map(item => {
            return String(item._id || item.refPatientId || '');
        }).filter(id => id);
        allRangePatientIds.push(...patientIds);
    });
    // Remove duplicates
    const uniquePatientIds = [...new Set(allRangePatientIds)];

    // Build custom data array dynamically from ranges
    const colorOptions = ["#636578", "#4879f5", "#636578", "#4879f5", "#636578"]; // Cycle through colors
    const customDataArray = [];
    let rangeIndex = 0;

    // Process all items in customDateRanges in order
    customDateRanges.forEach((item, index) => {
        if (item.type === "average") {
            // This is an average - calculate from range totals
            // Hide percentage for average items since they represent calculated averages, not counts
            customDataArray.push({
                _id: item.title,
                key: item.title,
                id: item.title,
                label: item.title,
                name: item.title,
                total: formatValue(averageValue),
                value: formatValue(averageValue),
                originalTotal: formatValue(averageValue),
                isSpacialItem: true,
                percentage: '',
                patientIds: uniquePatientIds,
                isTooltip: true,
                index: index,
                level: 0,
                isPercentageVisible: false // Hide percentage for average items
            });
        } else {
            // This is a regular range
            const rangeLabel = getDateRangeLabelFromRange(item);
            const patientsInRange = patientIdsByRange?.[rangeLabel] || [];
            const patientIds = patientsInRange.map(item => {
                // Use _id if available, otherwise use refPatientId, fallback to empty string
                return String(item._id || item.refPatientId || '');
            }).filter(id => id); // Remove empty strings

            customDataArray.push({
                _id: rangeLabel,
                label: rangeLabel,
                value: rangeDBData?.[rangeLabel] || 0,
                total: rangeDBData?.[rangeLabel] || 0,
                originalTotal: rangeDBData?.[rangeLabel] || 0,
                isTooltip: true,
                color: colorOptions[rangeIndex % colorOptions.length],
                percentage: itemPercentage(rangeDBData?.[rangeLabel] || 0, percentageTotal, "number"),
                patientIds: patientIds,
                level: 0,
                isSpacialItem: pageType === PAGE_TYPE.OVERALL ? false : true,
                index: index
            });
            rangeIndex++;
        }
    });

    return customDataArray;
}

/**
 * Build custom data array for time analysis
 * @param {Array} patientData - Array of patient data objects
 * @param {Array} customDateRanges - Array of time range objects
 * @param {number} percentageTotal - Total for percentage calculation
 * @returns {Array} - Array of data objects similar to shiftDataDynamic
 */
function buildCustomTimeDataArray(patientData, customDateRanges, percentageTotal, pageType) {
    // Add calculatedRange to each patient
    const patientDataWithRange = patientData.map(item => ({
        ...item,
        calculatedRange: getTimeRangeLabel(item, customDateRanges)
    }));

    // Group by range
    const rangeDBData = _.countBy(patientDataWithRange, "calculatedRange");
    const patientIdsByRange = _.groupBy(patientDataWithRange, "calculatedRange");

    // Build custom data array dynamically from customDateRanges
    const colorOptions = ["#636578", "#4879f5", "#636578", "#4879f5", "#636578"]; // Cycle through colors
    const customDataArray = customDateRanges.map((rangeOption, index) => {
        // Generate label if it doesn't exist
        const rangeLabel = getTimeRangeLabelFromRange(rangeOption);
        const patientsInRange = patientIdsByRange?.[rangeLabel] || [];
        const patientIds = patientsInRange.map(item => {
            // Use _id if available, otherwise use refPatientId, fallback to empty string
            return String(item._id || item.refPatientId || '');
        }).filter(id => id); // Remove empty strings

        return {
            _id: rangeLabel,
            label: rangeLabel,
            value: rangeDBData?.[rangeLabel] || 0,
            total: rangeDBData?.[rangeLabel] || 0,
            originalTotal: rangeDBData?.[rangeLabel] || 0,
            isTooltip: true,
            color: colorOptions[index % colorOptions.length],
            percentage: itemPercentage(rangeDBData?.[rangeLabel] || 0, percentageTotal, "number"),
            patientIds: patientIds,
            level: 0,
            isSpacialItem: pageType === PAGE_TYPE.OVERALL ? false : true,
            index: index
        };
    });

    return customDataArray;
}

export async function processFilterForCombineTab({
    filter,
    patientData,
    ninetyDaysData,
    result,
    customTab,
    sixtyDaysData,
    pageType,
    customCombineTabData = [],
    forComparison,
    totalFilterData,
    exhustListData = [],
    isCheckNewFilters = false,
    newSavedFilters = [],
    objectCustom = {}
}) {
    const normalizeId = (value) => {
        if (value == null) return null;
        const id = String(value).trim();
        if (!id || id === "undefined" || id === "null") return null;
        return id;
    };

    // Use customCombineTabData?.exhaustListData as fallback if exhustListData is not available
    const finalExhustListData = (exhustListData && Array.isArray(exhustListData) && exhustListData.length > 0)
        ? exhustListData
        : (customCombineTabData?.exhaustListData?.list && Array.isArray(customCombineTabData.exhaustListData.list) && customCombineTabData.exhaustListData.list.length > 0
            ? customCombineTabData.exhaustListData.list
            : []);
    const filters = customTab?.filters ?? [];
    const isCustomDateAnalysis = customTab?.isCustomDateAnalysis ?? false;
    const customDateRanges = customTab?.customDateRanges ?? [];
    const customAnalysisType = customTab?.customAnalysisType ?? "date";
    const customKey = customTab?.accessor;
    const operations = customTab?.operations ?? [];

    // Check if this card should skip filtering (already selected)
    const currentCardFilter = isCheckNewFilters && _.includes(newSavedFilters, customKey);
    if (currentCardFilter && objectCustom?.[customKey]) {
        // Use existing data from objectCustom instead of recalculating
        result[customKey] = objectCustom[customKey];
        return patientData;
    }

    // Check if exhaustAverageData card is selected (uses customDateRanges, same as 90-day analysis)
    const hasExhaustAverageCard = filters.some((filter) =>
        filter?.filters?.some(filterItem =>
            filterItem?.card?.value === OVERALL_CARDS_TYPE.EXHUST_AVARAGE_DATA
        )
    );
    // Use customDateRanges for exhaust average when exhaust average card is selected and not in custom date analysis mode
    const exhaustAverageRanges = (hasExhaustAverageCard && !isCustomDateAnalysis && customDateRanges && Array.isArray(customDateRanges) && customDateRanges.length > 0)
        ? customDateRanges.filter(item => item.type !== "average") // Filter out averages, only use ranges
        : [];

    // Helper: compute average days between dateOfADT and exhaustionDate for a list of patients
    const computeAverageExhaustDays = (dataList = []) => {
        if (!Array.isArray(dataList) || dataList.length === 0) return null;
        const dayDiffs = dataList
            .map(patient => {
                if (!patient?.dateOfADT || !patient?.exhaustionDate) return null;
                const start = moment(patient.dateOfADT).startOf('day');
                const end = moment(patient.exhaustionDate).startOf('day');
                if (!start.isValid() || !end.isValid()) return null;
                return end.diff(start, 'days') + 1; // inclusive
            })
            .filter(diff => diff !== null);
        if (dayDiffs.length === 0) return null;
        const sum = dayDiffs.reduce((acc, d) => acc + d, 0);
        return sum / dayDiffs.length;
    };

    let totalForPercentage = totalFilterData?.totalForPercentage;

    let resultData = [{
        _id: customKey,
        id: customKey,
        label: customTab?.label,
        name: customTab?.label,
        total: 0,
        value: 0,
        totalByPage: {},
        parentCard: customTab?.page,
        patientIds: [],
        percentage: 0,
        originalTotal: 0,
        isTooltip: false,
        isCustomDateAnalysis,
        customAnalysisType
    }];

    // Store results for each filter to apply operations
    let allFilterResults = [];
    // Accumulate all countDay values from ALL filters for average calculation
    // This ensures we calculate the average from ALL loop iterations across all filters, not just the last one
    let allCountDaysGlobal = [];

    for (let filterIndex = 0; filterIndex < filters.length; filterIndex++) {
        const filter = filters[filterIndex];
        let filterDashboard = filter?.dashboard;

        // Check if this filter contains exhaustInsuranceData or exhaustAverageData card - if so, use exhustListData as base
        const filterFilters = filter?.filters ?? [];
        const hasExhaustInsuranceCard = filterFilters.some(filterItem =>
            filterItem?.card?.value === OVERALL_CARDS_TYPE.EXHUAST_INSURANCE_DATA
        );
        const hasExhaustAverageCard = filterFilters.some(filterItem =>
            filterItem?.card?.value === OVERALL_CARDS_TYPE.EXHUST_AVARAGE_DATA
        );

        // Use finalExhustListData if exhaustInsuranceData or exhaustAverageData card is present, otherwise use patientData
        // finalExhustListData from backend is already filtered to contain only exhausted patients
        let currentPatientData = (hasExhaustInsuranceCard || hasExhaustAverageCard) && Array.isArray(finalExhustListData) && finalExhustListData.length > 0
            ? [...finalExhustListData]
            : [...patientData]; // Start with fresh data for each filter

        if (filterDashboard === pageType || (pageType === PAGE_TYPE.OVERALL && (filterDashboard === CUSTOM_TAB_PAGE_TYPE.OVERALL_INCOMING || filterDashboard === CUSTOM_TAB_PAGE_TYPE.OVERALL_OUTGOING))) {
            // Sort filters so that isMainCard items are processed last
            const sortedFilterFilters = [...filterFilters].sort((a, b) => {
                // If a is isMainCard, it should come after b (return 1)
                // If b is isMainCard, it should come after a (return -1)
                // If both or neither are isMainCard, maintain original order
                if (a.isMainCard && !b.isMainCard) return 1;
                if (!a.isMainCard && b.isMainCard) return -1;
                return 0;
            });
            // Track average count for exhaustAverageData card
            let exhaustAverageCount = null;
            // For NINETY_DAYS_DATA and SIXTY_DAYS_DATA: Accumulate all countDay values from all loop iterations
            // Use global accumulator to collect across all filters
            let allCountDays = [];

            for (const filterItem of sortedFilterFilters) {
                const { card, isMainCard } = filterItem;
                let items = filterItem?.items ?? [];
                if (!card?.value) continue;

                if (card?.questionId && (!isCustomDateAnalysis || (isCustomDateAnalysis && customAnalysisType === "date"))) {
                    let objectCustomRes = {};

                    const originalQuestion = filterItem?.question;
                    const key = card?.value;

                    if (!originalQuestion) continue;
                    const isCustomDateAnalysisAndDate = isCustomDateAnalysis && customAnalysisType === "date" && Array.isArray(customDateRanges) && customDateRanges.length > 0;
                    // For custom date analysis, build a new question object with customQuestionOptions derived from customDateRanges
                    const questionForProcessing =
                        isCustomDateAnalysisAndDate
                            ? {
                                ...originalQuestion,
                                customQuestionOptions: customDateRanges.map(range => ({
                                    min: String(range?.minDays ?? 0),
                                    max: String(range?.maxDays ?? 0),
                                    type: "range",
                                    title: ""
                                }))
                            }
                            : originalQuestion;

                    await processDynamicCard(questionForProcessing, currentPatientData, objectCustomRes, { ...totalFilterData, forComparison });

                    const resultItems = objectCustomRes?.[key] || [];
                    if (isCustomDateAnalysisAndDate) {
                        items = resultItems?.map(item => item?._id);
                    }
                    const patientIds = resultItems.reduce((acc, ele) => {
                        if (_.includes(items, ele?._id)) {
                            acc.push(...(ele?.patientIds || []));
                        }
                        return acc;
                    }, []);

                    currentPatientData = await filterCustomPatientData(
                        currentPatientData,
                        { [key]: items },
                        {
                            type: key,
                            patientIds,
                            question: { customQuestionInputType: questionForProcessing.customQuestionInputType },
                        },
                        true
                    );
                } else {
                    // Special case: exhaustInsuranceData card - if it has items, filter by insurance
                    // Since we're already using exhustListData as base, we just need to filter by selected items
                    if (card?.value === OVERALL_CARDS_TYPE.EXHUAST_INSURANCE_DATA && items && items.length > 0) {
                        // Filter exhaust data by selected insurance items
                        currentPatientData = currentPatientData.filter(patient => {
                            // Check if patient's insurance matches any selected item
                            const payerSourceInsuranceId = patient?.payerSourceInsurance?._id || patient?.payerSourceInsurance;
                            const secondaryInsuranceId = patient?.secondaryInsurance?._id || patient?.secondaryInsurance;
                            return _.includes(items, payerSourceInsuranceId) || _.includes(items, secondaryInsuranceId);
                        });
                    } else if (card?.value === OVERALL_CARDS_TYPE.EXHUST_AVARAGE_DATA) {
                        // Special case: exhaustAverageData card - calculate average days between dateOfADT and exhaustionDate per range
                        // exhaustAverageRanges are stored at top level (same as customDateRanges) and filter by count field
                        // Filter patients by dateOfADT -> exhaustionDate difference only when ranges exist
                        let dataForAverage = currentPatientData;
                        if (exhaustAverageRanges && Array.isArray(exhaustAverageRanges) && exhaustAverageRanges.length > 0) {
                            dataForAverage = currentPatientData.filter(patient => {
                                if (!patient?.dateOfADT || !patient?.exhaustionDate) return false;
                                const start = moment(patient.dateOfADT).startOf('day');
                                const end = moment(patient.exhaustionDate).startOf('day');
                                if (!start.isValid() || !end.isValid()) return false;
                                const days = end.diff(start, 'days') + 1;
                                return exhaustAverageRanges.some(range => {
                                    const min = Number(range?.minDays ?? 0);
                                    const max = Number(range?.maxDays ?? (range?.minDays ?? 0));
                                    if (Number.isNaN(min) || Number.isNaN(max)) return false;
                                    return days >= min && days <= max;
                                });
                            });
                        }

                        if (exhaustAverageRanges && Array.isArray(exhaustAverageRanges) && exhaustAverageRanges.length > 0) {
                            // Capture filtered data to avoid unsafe reference in loop
                            const dataSnapshot = dataForAverage;
                            // For each range, calculate average days difference for patients in that range
                            const rangeAverages = exhaustAverageRanges.map(range => {
                                const min = Number(range?.minDays ?? 0);
                                const max = Number(range?.maxDays ?? (range?.minDays ?? 0));
                                if (Number.isNaN(min) || Number.isNaN(max)) return null;

                                // Find patients in this range based on their date difference
                                const patientsInRange = dataSnapshot.filter(patient => {
                                    if (!patient?.dateOfADT || !patient?.exhaustionDate) return false;
                                    const start = moment(patient.dateOfADT).startOf('day');
                                    const end = moment(patient.exhaustionDate).startOf('day');
                                    if (!start.isValid() || !end.isValid()) return false;
                                    const days = end.diff(start, 'days') + 1;
                                    return days >= min && days <= max;
                                });

                                if (patientsInRange.length === 0) return null;

                                // Calculate days difference for each patient (dateOfADT to exhaustionDate)
                                const dayDifferences = patientsInRange
                                    .map(patient => {
                                        if (!patient?.dateOfADT || !patient?.exhaustionDate) return null;
                                        const start = moment(patient.dateOfADT).startOf('day');
                                        const end = moment(patient.exhaustionDate).startOf('day');
                                        if (!start.isValid() || !end.isValid()) return null;
                                        // Calculate difference in days (inclusive: +1)
                                        return end.diff(start, 'days') + 1;
                                    })
                                    .filter(days => days !== null);

                                if (dayDifferences.length === 0) return null;

                                // Calculate average days for this range
                                const sum = dayDifferences.reduce((acc, days) => acc + days, 0);
                                return sum / dayDifferences.length;
                            }).filter(avg => avg !== null);

                            // Calculate overall average across all ranges
                            if (rangeAverages.length > 0) {
                                const sumOfAverages = rangeAverages.reduce((sum, avg) => sum + avg, 0);
                                exhaustAverageCount = sumOfAverages / rangeAverages.length;
                            } else {
                                exhaustAverageCount = 0;
                            }

                            // Use only patients that fall into any of the ranges for further processing
                            currentPatientData = dataForAverage;
                        }
                        // If no exhaustAverageRanges, use all exhustListData (already filtered above)
                    } else if (isCustomDateAnalysis && (card?.value === HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA || card?.value === CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA) && Array.isArray(customDateRanges) && customDateRanges.length > 0 && (isCustomDateAnalysis && isMainCard)) {
                        // Custom Date Analysis: use custom day ranges instead of items for ninetyDaysData/sixtyDaysData
                        // For NINETY_DAYS_DATA and SIXTY_DAYS_DATA: Calculate average from currentPatientData BEFORE filtering by ranges
                        // currentPatientData at this point has been filtered by all non-main card filters
                        const hasAverageType = customDateRanges.some(range => range?.type === "average");

                        if (hasAverageType) {
                            // Accumulate countDay values from currentPatientData (already filtered by non-main card filters)
                            // Collect all countDay values from this iteration to merge with all previous iterations
                            const countDays = (currentPatientData || [])
                                .map(patient => {
                                    const countDay = Number(patient?.countDay);
                                    return Number.isNaN(countDay) ? null : countDay;
                                })
                                .filter(countDay => countDay !== null);
                            // Accumulate countDays from this iteration into both local and global arrays
                            if (countDays.length > 0) {
                                allCountDays = [...allCountDays, ...countDays];
                                allCountDaysGlobal = [...allCountDaysGlobal, ...countDays];
                            }
                        }

                        // Separate "average" type from "range" types
                        // Only filter by "range" types, not "average" types
                        const rangeTypesOnly = customDateRanges.filter(range => range?.type !== "average");

                        if (rangeTypesOnly.length > 0) {
                            // Filter by range types only
                            currentPatientData = await applyCustomDateRangeFilter(currentPatientData, rangeTypesOnly, "countDay");
                        }
                        // If only "average" type exists, don't filter - use all patients
                        // The average will be used from daysDataAverageFromAllFiltered in the total calculation
                    } else if (isCustomDateAnalysis && customAnalysisType === "time" && Array.isArray(customDateRanges) && customDateRanges.length > 0 && (isCustomDateAnalysis && isMainCard)) {
                        let timeField = card?.value === "shiftData" ? "transferTime" : card?.value;
                        currentPatientData = applyCustomTimeRangeFilter(currentPatientData, customDateRanges, timeField);
                    } else {
                        currentPatientData = await applyFilter(card?.value, items, currentPatientData, ninetyDaysData, sixtyDaysData, forComparison);
                    }
                }
            }

            // Calculate average from all accumulated countDay values after the loop completes
            // This ensures we calculate the average from ALL loop iterations, not just the last one
            let daysDataAverageFromAllFiltered = null;
            if (allCountDays.length > 0) {
                const sum = allCountDays.reduce((acc, days) => acc + days, 0);
                daysDataAverageFromAllFiltered = sum / allCountDays.length;
            }

            // Extract patient IDs first - exhaust data has both _id and id, check both
            // Deduplicate IDs within this filter result
            const patientIdSet = new Set();
            (currentPatientData || []).forEach(item => {
                const id = item._id || item.id;
                if (id != null) {
                    const strId = String(id).trim();
                    if (strId && strId !== 'undefined' && strId !== 'null') {
                        patientIdSet.add(strId);
                    }
                }
            });
            const currentPatientIds = Array.from(patientIdSet);

            // For exhaustAverageData, show average days (when available) otherwise use unique patient count
            // Use unique patient count instead of array length to handle duplicate patient records
            let total = currentPatientIds.length || 0;

            // Check if this filter result is for NINETY_DAYS_DATA or SIXTY_DAYS_DATA with "average" type
            // Use the pre-calculated average from all filtered patients (before main card processing)
            const hasDaysDataWithAverage = sortedFilterFilters.some(
                item => item.isMainCard &&
                    (item?.card?.value === HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA || item?.card?.value === CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA) &&
                    isCustomDateAnalysis &&
                    Array.isArray(customDateRanges) &&
                    customDateRanges.some(range => range?.type === "average")
            );

            if (hasDaysDataWithAverage && daysDataAverageFromAllFiltered !== null) {
                // Use pre-calculated average from all filtered patients (before main card processing)
                total = daysDataAverageFromAllFiltered;
            } else if (exhaustAverageCount !== null) {
                total = exhaustAverageCount;
            } else if (hasExhaustAverageCard) {
                // Only compute average exhaust days if this is actually an exhaust average card
                // hasExhaustAverageCard is already declared earlier in the function scope
                const avgDays = computeAverageExhaustDays(currentPatientData);
                if (avgDays !== null) total = avgDays;
            }
            // Otherwise, total remains as currentPatientIds.length (unique patient count)
            // For combineTab: Always use totalForPercentage (mainNumPercentage), never fall back to total
            // Falling back to total would make percentage 100% (total/total = 100%)
            const percentageTotal = totalForPercentage || (customTab?.type === CUSTOM_TAB_TYPE.combineTab ? null : total);

            allFilterResults.push({
                total,
                patientIds: currentPatientIds, // Already normalized strings from patientIdSet
                patientData: currentPatientData || [], // Store actual patient data for customDataArray
                dashboard: filterDashboard,
                filterIndex: filterIndex, // Include filter index to handle same dashboard appearing multiple times
                percentage: itemPercentage(total, percentageTotal, "number"),
                // Store pre-calculated average for NINETY_DAYS_DATA/SIXTY_DAYS_DATA if available
                daysDataAverageFromAllFiltered: hasDaysDataWithAverage ? daysDataAverageFromAllFiltered : null,
                label: filter?.label || null // Store label from filter group
            });
        }

        if (filterDashboard !== pageType && !(pageType === PAGE_TYPE.OVERALL && (filterDashboard === CUSTOM_TAB_PAGE_TYPE.OVERALL_INCOMING || filterDashboard === CUSTOM_TAB_PAGE_TYPE.OVERALL_OUTGOING))) {
            // Check if this filter contains exhaustInsuranceData or exhaustAverageData card - if so, use exhustListData as base
            const filterFiltersArray = filter?.filters ?? [];
            const hasExhaustInsuranceCard = filterFiltersArray.some(filterItem =>
                filterItem?.card?.value === OVERALL_CARDS_TYPE.EXHUAST_INSURANCE_DATA
            );
            const hasExhaustAverageCard = filterFiltersArray.some(filterItem =>
                filterItem?.card?.value === OVERALL_CARDS_TYPE.EXHUST_AVARAGE_DATA
            );

            // Use finalExhustListData if exhaustInsuranceData or exhaustAverageData card is present, otherwise use dashboardData
            // finalExhustListData from backend is already filtered to contain only exhausted patients
            // Create a fresh copy of the data source for each filter group to ensure independence when same dashboard appears multiple times
            let dashboardData = (hasExhaustInsuranceCard || hasExhaustAverageCard) && Array.isArray(finalExhustListData) && finalExhustListData.length > 0
                ? [...finalExhustListData]
                : (Array.isArray(customCombineTabData[filterDashboard]?.list)
                    ? [...customCombineTabData[filterDashboard].list]
                    : []);

            // Overall page: apply current overall scope to other dashboards too.
            // This ensures non-overall filter dashboards are constrained by the same selected patient set.
            if (pageType === PAGE_TYPE.OVERALL && Array.isArray(patientData) && patientData.length > 0 && dashboardData.length > 0) {
                const overallScopedIds = new Set(
                    patientData
                        .map(item => normalizeId(item?._id || item?.id || item?.refPatientId))
                        .filter(Boolean)
                );

                if (overallScopedIds.size > 0) {
                    dashboardData = dashboardData.filter(item => {
                        const candidateIds = [
                            normalizeId(item?.refPatientId),
                            normalizeId(item?._id),
                            normalizeId(item?.id),
                        ].filter(Boolean);

                        return candidateIds.some(id => overallScopedIds.has(id));
                    });
                }
            }
            const dashboardNinetyDays = Array.isArray(customCombineTabData[filterDashboard]?.ninetyDaysData)
                ? [...customCombineTabData[filterDashboard].ninetyDaysData]
                : [];
            const dashboardSixtyDays = Array.isArray(customCombineTabData[filterDashboard]?.sixtyDaysData)
                ? [...customCombineTabData[filterDashboard].sixtyDaysData]
                : [];

            let objectCustomRes = {};
            // Sort filters so that isMainCard items are processed last
            const sortedFiltersArray = [...filterFiltersArray].sort((a, b) => {
                // If a is isMainCard, it should come after b (return 1)
                // If b is isMainCard, it should come after a (return -1)
                // If both or neither are isMainCard, maintain original order
                if (a.isMainCard && !b.isMainCard) return 1;
                if (!a.isMainCard && b.isMainCard) return -1;
                return 0;
            });
            // Track average count for exhaustAverageData card
            let exhaustAverageCount = null;
            // For NINETY_DAYS_DATA and SIXTY_DAYS_DATA: Store average calculated from all filtered patients before main card processing
            // Accumulate all countDay values from all loop iterations
            // Use global accumulator to collect across all filters
            let allCountDays = [];

            for (const filterItem of sortedFiltersArray) {
                const { card, isMainCard } = filterItem;
                let items = filterItem?.items ?? [];
                if (!card?.value) continue;

                if (card?.questionId && (!isCustomDateAnalysis || (isCustomDateAnalysis && customAnalysisType === "date"))) {
                    const originalQuestion = filterItem?.question;
                    const key = card?.value;

                    if (!originalQuestion) continue;
                    // For custom date analysis, build a new question object with customQuestionOptions derived from customDateRanges
                    const isCustomDateAnalysisAndDate = isCustomDateAnalysis && customAnalysisType === "date" && Array.isArray(customDateRanges) && customDateRanges.length > 0;
                    const questionForProcessing =
                        isCustomDateAnalysisAndDate ? {
                            ...originalQuestion,
                            customQuestionOptions: customDateRanges.map(range => ({
                                min: String(range?.minDays ?? 0),
                                max: String(range?.maxDays ?? 0),
                                type: "range",
                                title: ""
                            }))
                        }
                            : originalQuestion;
                    await processDynamicCard(questionForProcessing, dashboardData, objectCustomRes, { ...totalFilterData, forComparison });
                    const resultItems = objectCustomRes?.[key] || [];
                    if (isCustomDateAnalysisAndDate) {
                        items = resultItems?.map(item => item?._id);
                    }
                    const patientIds = resultItems.reduce((acc, ele) => {
                        if (_.includes(items, ele?._id)) {
                            acc.push(...(ele?.patientIds || []));
                        }
                        return acc;
                    }, []);

                    dashboardData = filterCustomPatientData(
                        dashboardData,
                        { [key]: items },
                        {
                            type: key,
                            patientIds,
                            question: { customQuestionInputType: questionForProcessing.customQuestionInputType },
                        },
                        true
                    );

                } else {
                    // Special case: exhaustInsuranceData card - if it has items, filter by insurance
                    // Since we're already using exhustListData as base, we just need to filter by selected items
                    if (card?.value === OVERALL_CARDS_TYPE.EXHUAST_INSURANCE_DATA && items && items.length > 0) {
                        // Filter exhaust data by selected insurance items
                        dashboardData = dashboardData.filter(patient => {
                            // Check if patient's insurance matches any selected item
                            const payerSourceInsuranceId = patient?.payerSourceInsurance?._id || patient?.payerSourceInsurance;
                            const secondaryInsuranceId = patient?.secondaryInsurance?._id || patient?.secondaryInsurance;
                            return _.includes(items, payerSourceInsuranceId) || _.includes(items, secondaryInsuranceId);
                        });
                    } else if (card?.value === OVERALL_CARDS_TYPE.EXHUST_AVARAGE_DATA) {
                        // Special case: exhaustAverageData card - calculate average days between dateOfADT and exhaustionDate per range
                        // exhaustAverageRanges are stored at top level (same as customDateRanges) and filter by count field
                        // Filter patients by dateOfADT -> exhaustionDate difference only when ranges exist
                        let dataForAverage = dashboardData;
                        if (exhaustAverageRanges && Array.isArray(exhaustAverageRanges) && exhaustAverageRanges.length > 0) {
                            dataForAverage = dashboardData.filter(patient => {
                                if (!patient?.dateOfADT || !patient?.exhaustionDate) return false;
                                const start = moment(patient.dateOfADT).startOf('day');
                                const end = moment(patient.exhaustionDate).startOf('day');
                                if (!start.isValid() || !end.isValid()) return false;
                                const days = end.diff(start, 'days') + 1;
                                return exhaustAverageRanges.some(range => {
                                    const min = Number(range?.minDays ?? 0);
                                    const max = Number(range?.maxDays ?? (range?.minDays ?? 0));
                                    if (Number.isNaN(min) || Number.isNaN(max)) return false;
                                    return days >= min && days <= max;
                                });
                            });
                        }

                        if (exhaustAverageRanges && Array.isArray(exhaustAverageRanges) && exhaustAverageRanges.length > 0) {
                            // Capture filtered data to avoid unsafe reference in loop
                            const dataSnapshot = dataForAverage;
                            // For each range, calculate average days difference for patients in that range
                            const rangeAverages = exhaustAverageRanges.map(range => {
                                const min = Number(range?.minDays ?? 0);
                                const max = Number(range?.maxDays ?? (range?.minDays ?? 0));
                                if (Number.isNaN(min) || Number.isNaN(max)) return null;

                                // Find patients in this range based on their date difference
                                const patientsInRange = dataSnapshot.filter(patient => {
                                    if (!patient?.dateOfADT || !patient?.exhaustionDate) return false;
                                    const start = moment(patient.dateOfADT).startOf('day');
                                    const end = moment(patient.exhaustionDate).startOf('day');
                                    if (!start.isValid() || !end.isValid()) return false;
                                    const days = end.diff(start, 'days') + 1;
                                    return days >= min && days <= max;
                                });

                                if (patientsInRange.length === 0) return null;

                                // Calculate days difference for each patient (dateOfADT to exhaustionDate)
                                const dayDifferences = patientsInRange
                                    .map(patient => {
                                        if (!patient?.dateOfADT || !patient?.exhaustionDate) return null;
                                        const start = moment(patient.dateOfADT).startOf('day');
                                        const end = moment(patient.exhaustionDate).startOf('day');
                                        if (!start.isValid() || !end.isValid()) return null;
                                        // Calculate difference in days (inclusive: +1)
                                        return end.diff(start, 'days') + 1;
                                    })
                                    .filter(days => days !== null);

                                if (dayDifferences.length === 0) return null;

                                // Calculate average days for this range
                                const sum = dayDifferences.reduce((acc, days) => acc + days, 0);
                                return sum / dayDifferences.length;
                            }).filter(avg => avg !== null);

                            // Calculate overall average across all ranges
                            if (rangeAverages.length > 0) {
                                const sumOfAverages = rangeAverages.reduce((sum, avg) => sum + avg, 0);
                                exhaustAverageCount = sumOfAverages / rangeAverages.length;
                            } else {
                                exhaustAverageCount = 0;
                            }

                            // Use only patients that fall into any of the ranges for further processing
                            dashboardData = dataForAverage;
                        }
                        // If no exhaustAverageRanges, use all exhustListData (already filtered above)
                    } else if (isCustomDateAnalysis && (card?.value === HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA || card?.value === CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA) && Array.isArray(customDateRanges) && customDateRanges.length > 0 && (isCustomDateAnalysis && isMainCard)) {
                        // Custom Date Analysis: use custom day ranges instead of items for ninetyDaysData/sixtyDaysData
                        // For NINETY_DAYS_DATA and SIXTY_DAYS_DATA: Calculate average from dashboardData BEFORE filtering by ranges
                        // dashboardData at this point has been filtered by all non-main card filters
                        const hasAverageType = customDateRanges.some(range => range?.type === "average");

                        if (hasAverageType) {
                            // Accumulate countDay values from dashboardData (already filtered by non-main card filters)
                            // Collect all countDay values from this iteration to merge with all previous iterations
                            const countDays = (dashboardData || [])
                                .map(patient => {
                                    const countDay = Number(patient?.countDay);
                                    return Number.isNaN(countDay) ? null : countDay;
                                })
                                .filter(countDay => countDay !== null);
                            // Accumulate countDays from this iteration into both local and global arrays
                            if (countDays.length > 0) {
                                allCountDays = [...allCountDays, ...countDays];
                                allCountDaysGlobal = [...allCountDaysGlobal, ...countDays];
                            }
                        }

                        // Separate "average" type from "range" types
                        // Only filter by "range" types, not "average" types
                        const rangeTypesOnly = customDateRanges.filter(range => range?.type !== "average");

                        if (rangeTypesOnly.length > 0) {
                            // Filter by range types only
                            dashboardData = await applyCustomDateRangeFilter(dashboardData, rangeTypesOnly, "countDay");
                        }
                        // If only "average" type exists, don't filter - use all patients
                        // The average will be used from daysDataAverageFromAllFiltered in the total calculation
                    } else if (isCustomDateAnalysis && customAnalysisType === "time" && Array.isArray(customDateRanges) && customDateRanges.length > 0 && (isCustomDateAnalysis && isMainCard)) {
                        let timeField = card?.value === "shiftData" ? "transferTime" : card?.value;
                        dashboardData = applyCustomTimeRangeFilter(dashboardData, customDateRanges, timeField);
                    } else {
                        dashboardData = await applyFilter(card?.value, items, dashboardData, dashboardNinetyDays, dashboardSixtyDays, forComparison);
                    }
                }
            }

            // Calculate average from all accumulated countDay values after the loop completes
            // This ensures we calculate the average from ALL loop iterations, not just the last one
            let daysDataAverageFromAllFiltered = null;
            if (allCountDays.length > 0) {
                const sum = allCountDays.reduce((acc, days) => acc + days, 0);
                daysDataAverageFromAllFiltered = sum / allCountDays.length;
            }

            // Extract patient IDs first - exhaust data has both _id and id, check both
            // Deduplicate IDs within this filter result
            const patientIdSet = new Set();
            (dashboardData || []).forEach(item => {
                const id = item._id || item.id;
                if (id != null) {
                    const strId = String(id).trim();
                    if (strId && strId !== 'undefined' && strId !== 'null') {
                        patientIdSet.add(strId);
                    }
                }
            });
            const currentPatientIds = Array.from(patientIdSet);

            // For exhaustAverageData, show average days (when available) otherwise use unique patient count
            // Use unique patient count instead of array length to handle duplicate patient records
            let total = currentPatientIds.length || 0;

            // Check if this filter result is for NINETY_DAYS_DATA or SIXTY_DAYS_DATA with "average" type
            // Use the pre-calculated average from all filtered patients (before main card processing)
            const hasDaysDataWithAverage = sortedFiltersArray.some(
                item => item.isMainCard &&
                    (item?.card?.value === HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA || item?.card?.value === CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA) &&
                    isCustomDateAnalysis &&
                    Array.isArray(customDateRanges) &&
                    customDateRanges.some(range => range?.type === "average")
            );

            if (hasDaysDataWithAverage && daysDataAverageFromAllFiltered !== null) {
                // Use pre-calculated average from all filtered patients (before main card processing)
                total = daysDataAverageFromAllFiltered;
            } else if (exhaustAverageCount !== null) {
                total = exhaustAverageCount;
            } else if (hasExhaustAverageCard) {
                // Only compute average exhaust days if this is actually an exhaust average card
                const avgDays = computeAverageExhaustDays(dashboardData);
                if (avgDays !== null) total = avgDays;
            }
            // Otherwise, total remains as currentPatientIds.length (unique patient count)
            // For combineTab: Always use totalForPercentage (mainNumPercentage), never fall back to total
            // Falling back to total would make percentage 100% (total/total = 100%)
            const percentageTotal = totalForPercentage || (customTab?.type === CUSTOM_TAB_TYPE.combineTab ? null : total);

            allFilterResults.push({
                total,
                patientIds: currentPatientIds,
                patientData: dashboardData || [], // Store actual patient data for customDataArray
                dashboard: filterDashboard,
                filterIndex: filterIndex, // Include filter index to handle same dashboard appearing multiple times
                percentage: itemPercentage(total, percentageTotal, "number"),
                // Store pre-calculated average for NINETY_DAYS_DATA/SIXTY_DAYS_DATA if available
                daysDataAverageFromAllFiltered: hasDaysDataWithAverage ? daysDataAverageFromAllFiltered : null,
                label: filter?.label || null // Store label from filter group
            });
        }
    }
    // Helper function to normalize patient IDs to strings for consistent Set operations
    // This is critical when the same dashboard appears multiple times
    const normalizePatientId = (id) => {
        if (id == null || id === undefined) return null;
        const strId = String(id).trim();
        return (strId && strId !== 'undefined' && strId !== 'null' && strId !== '') ? strId : null;
    };

    // Apply operations between filters
    if (allFilterResults.length > 0) {
        // Normalize and initialize with first filter result
        const firstPatientIds = (allFilterResults[0].patientIds || [])
            .map(normalizePatientId)
            .filter(id => id !== null);

        let finalTotal = allFilterResults[0].total || 0;
        let finalPatientIds = new Set(firstPatientIds);

        // Apply operations between consecutive filters
        let i = 1;
        while (i < allFilterResults.length) {
            const operation = operations[i - 1] || 'plus'; // Default to plus if operation not specified
            const currentFilterResult = allFilterResults[i];
            const currentTotal = currentFilterResult.total || 0;

            // Normalize patient IDs from current filter result
            const currentPatientIds = (currentFilterResult.patientIds || [])
                .map(normalizePatientId)
                .filter(id => id !== null);

            if (operation === 'plus') {
                // Plus operation: Add totals directly
                // This works correctly for both:
                // - Same dashboard: Sum totals even if patient IDs overlap (e.g., 2 + 3 = 5)
                // - Different dashboards: Sum totals (e.g., hospital 2 + community 3 = 5)
                // Patient IDs are tracked in Set for reference, but total is sum of all totals
                finalTotal = finalTotal + currentTotal;

                // Add patient IDs to the set (union) for tracking
                for (const id of currentPatientIds) {
                    if (id) {
                        finalPatientIds.add(id);
                    }
                }
                i++;
            } else if (operation === 'minus') {
                // Minus operation: Subtract totals directly
                // This works correctly for both:
                // - Same dashboard: Subtract totals even if patient IDs overlap (e.g., 5 - 3 = 2)
                // - Different dashboards: Subtract totals (e.g., hospital 5 - community 3 = 2)
                // Patient IDs are removed from Set for tracking, but total is difference of totals
                finalTotal = finalTotal - currentTotal;

                // Remove patients from the set (set difference) for tracking
                for (const id of currentPatientIds) {
                    if (id) {
                        finalPatientIds.delete(id);
                    }
                }

                // Allow negative values - don't set to 0
                // The display components will handle showing the minus sign
                i++;
            } else if (operation === 'average') {
                // Special case: Check for consecutive 'average' operations
                // If we have consecutive averages, average all values together
                // Example: ['average', 'average'] with [7, 34, 5] = (7+34+5)/3 = 15.33

                // Collect all values that will be averaged together
                const averageValues = [finalTotal]; // Start with current finalTotal
                const averagePatientIds = new Set(finalPatientIds);
                let consecutiveAverageCount = 0;

                // Look ahead to find all consecutive 'average' operations
                for (let j = i - 1; j < operations.length && operations[j] === 'average'; j++) {
                    const idx = j + 1; // Index in allFilterResults
                    if (idx < allFilterResults.length) {
                        averageValues.push(allFilterResults[idx].total || 0);
                        // Normalize patient IDs for each consecutive average result
                        const avgPatientIds = (allFilterResults[idx].patientIds || [])
                            .map(normalizePatientId)
                            .filter(id => id !== null);
                        for (const id of avgPatientIds) {
                            if (id) {
                                averagePatientIds.add(id);
                            }
                        }
                        consecutiveAverageCount++;
                    }
                }

                // Calculate average of all collected values
                const sum = averageValues.reduce((acc, val) => acc + val, 0);
                finalTotal = sum / averageValues.length;

                // Update patient IDs to union of all averaged values
                finalPatientIds = averagePatientIds;

                // Skip all processed consecutive average operations
                i += consecutiveAverageCount;
            }
        }

        // Update result data
        resultData[0].total = finalTotal;
        resultData[0].value = finalTotal;
        resultData[0].patientIds = Array.from(finalPatientIds);

        // Build totalByPage with individual dashboard results
        // Use unique key combining dashboard and filterIndex to handle same dashboard appearing multiple times
        for (const filterResult of allFilterResults) {
            const uniqueKey = `${filterResult.dashboard}_${filterResult.filterIndex}`;
            resultData[0].totalByPage[uniqueKey] = {
                total: filterResult.total,
                percentage: filterResult.percentage,
                dashboard: filterResult.dashboard, // Keep dashboard name for reference
                patientIds: filterResult.patientIds || [], // Store patientIds for each dashboard entry
                label: filterResult.label || null // Store label if available
            };
        }

        // For combineTab: Always use totalForPercentage (mainNumPercentage), never fall back to finalTotal
        // Falling back to finalTotal would make percentage 100% (finalTotal/finalTotal = 100%)
        const finalPercentageTotal = totalForPercentage || (customTab?.type === CUSTOM_TAB_TYPE.combineTab ? null : Math.abs(finalTotal));
        resultData[0].percentage = itemPercentage(finalTotal, finalPercentageTotal || Math.abs(finalTotal), "number");

        // Build customDataArray when isCustomDateAnalysis is true
        if (isCustomDateAnalysis && Array.isArray(customDateRanges) && customDateRanges.length > 0) {
            // Collect all patient data from filtered results that match finalPatientIds
            let allPatientData = [];

            // Collect patient data from all filter results that match finalPatientIds
            for (const filterResult of allFilterResults) {
                const filterPatientData = filterResult.patientData || [];
                // Filter to only include patients that are in finalPatientIds
                const matchingPatients = filterPatientData.filter(item => {
                    const itemId = String(item._id || '');
                    const refPatientId = String(item.refPatientId || '');
                    return finalPatientIds.has(itemId) || finalPatientIds.has(refPatientId);
                });
                allPatientData.push(...matchingPatients);
            }

            // Remove duplicates based on _id, but keep refPatientId matches if _id doesn't exist
            const uniquePatientData = [];
            const seenIds = new Set();
            for (const item of allPatientData) {
                const itemId = String(item._id || '');
                const refPatientId = String(item.refPatientId || '');
                // Use _id as primary key, fallback to refPatientId
                const key = itemId || refPatientId;
                if (key && !seenIds.has(key)) {
                    seenIds.add(key);
                    uniquePatientData.push(item);
                }
            }

            // Extract intermediateTotals and cardTitles from totalFilterData for level totals
            const intermediateTotals = totalFilterData?.intermediateTotals || {};
            const cardTitles = totalFilterData?.cardTitles || {};
            const currentLevel = totalFilterData?.currentLevel || 0;

            // Check if any filter result had NINETY_DAYS_DATA or SIXTY_DAYS_DATA with "average" type
            // Use the pre-calculated average from filter results instead of recalculating
            // (because uniquePatientData only contains patients after range filtering)
            let daysDataPreCalculatedAverage = null;
            const hasDaysDataWithAverage = allFilterResults.some(
                result => {
                    // Check if this result came from a filter with NINETY_DAYS_DATA or SIXTY_DAYS_DATA main card
                    const filterFilters = filters[result.filterIndex]?.filters || [];
                    const hasDaysDataMainCard = filterFilters.some(
                        item => item.isMainCard &&
                            (item?.card?.value === HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA || item?.card?.value === CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA)
                    );
                    const hasAverageType = customDateRanges.some(range => range?.type === "average");
                    return hasDaysDataMainCard && hasAverageType;
                }
            );

            // If we have NINETY_DAYS_DATA or SIXTY_DAYS_DATA with average type, use the pre-calculated average from ALL filters combined
            if (hasDaysDataWithAverage && customDateRanges.some(range => range?.type === "average")) {
                // Calculate average from all accumulated countDay values across ALL filters
                // This ensures we use the average from ALL loop iterations, not just one filter
                if (allCountDaysGlobal.length > 0) {
                    const sum = allCountDaysGlobal.reduce((acc, days) => acc + days, 0);
                    daysDataPreCalculatedAverage = sum / allCountDaysGlobal.length;
                } else {
                    // Fallback: Find the filter result that has the pre-calculated average
                    const resultWithAverage = allFilterResults.find(
                        result => result.daysDataAverageFromAllFiltered !== null && result.daysDataAverageFromAllFiltered !== undefined
                    );

                    if (resultWithAverage) {
                        // Use the pre-calculated average that was calculated from all filtered patients
                        // (before range filtering)
                        daysDataPreCalculatedAverage = resultWithAverage.daysDataAverageFromAllFiltered;
                    }
                }
            }

            // Build customDataArray based on customAnalysisType
            let customDataArray = [];
            if (customAnalysisType === "date") {
                // Group by date ranges (countDay)
                // Pass the pre-calculated average for NINETY_DAYS_DATA if available
                customDataArray = buildCustomDateDataArray(
                    uniquePatientData,
                    customDateRanges,
                    totalForPercentage || Math.abs(finalTotal),
                    pageType,
                    daysDataPreCalculatedAverage
                );
            } else if (customAnalysisType === "time") {
                // Group by time ranges (transferTime)
                customDataArray = buildCustomTimeDataArray(uniquePatientData, customDateRanges, totalForPercentage || Math.abs(finalTotal), pageType);
            }

            // Attach level totals to customDataArray items if intermediateTotals are available
            if (customDataArray.length > 0 && currentLevel >= 2 && intermediateTotals) {
                const level1 = Array.isArray(intermediateTotals.level1) ? intermediateTotals.level1 : [];
                const level2 = Array.isArray(intermediateTotals.level2) ? intermediateTotals.level2 : [];
                const level3 = Array.isArray(intermediateTotals.level3) ? intermediateTotals.level3 : [];
                const level4 = Array.isArray(intermediateTotals.level4) ? intermediateTotals.level4 : [];
                const level5 = Array.isArray(intermediateTotals.level5) ? intermediateTotals.level5 : [];

                // Convert intermediate level patient arrays to Sets of patient IDs for faster lookup
                const level1PatientIds = new Set(level1.map(p => String(p._id || p.id || '')));
                const level2PatientIds = new Set(level2.map(p => String(p._id || p.id || '')));
                const level3PatientIds = new Set(level3.map(p => String(p._id || p.id || '')));
                const level4PatientIds = new Set(level4.map(p => String(p._id || p.id || '')));
                const level5PatientIds = new Set(level5.map(p => String(p._id || p.id || '')));

                // Attach level totals to each item in customDataArray
                customDataArray = customDataArray.map(item => {
                    const itemPatientIds = Array.isArray(item.patientIds) ? item.patientIds.map(id => String(id)) : [];

                    // Count how many patientIds from this item exist in each intermediate level
                    const level1Total = itemPatientIds.filter(id => level1PatientIds.has(id)).length;
                    const level2Total = itemPatientIds.filter(id => level2PatientIds.has(id)).length;
                    const level3Total = itemPatientIds.filter(id => level3PatientIds.has(id)).length;
                    const level4Total = itemPatientIds.filter(id => level4PatientIds.has(id)).length;
                    const level5Total = itemPatientIds.filter(id => level5PatientIds.has(id)).length;

                    return {
                        ...item,
                        ...(currentLevel >= 2 && { level1Total }),
                        ...(currentLevel >= 3 && { level2Total }),
                        ...(currentLevel >= 4 && { level3Total }),
                        ...(currentLevel >= 5 && { level4Total }),
                        ...(currentLevel >= 6 && { level5Total }),
                        ...(currentLevel >= 2 && cardTitles.level1 && { level1CardTitle: cardTitles.level1 }),
                        ...(currentLevel >= 3 && cardTitles.level2 && { level2CardTitle: cardTitles.level2 }),
                        ...(currentLevel >= 4 && cardTitles.level3 && { level3CardTitle: cardTitles.level3 }),
                        ...(currentLevel >= 5 && cardTitles.level4 && { level4CardTitle: cardTitles.level4 }),
                        ...(currentLevel >= 6 && cardTitles.level5 && { level5CardTitle: cardTitles.level5 }),
                    };
                });
            }

            resultData[0].customDataArray = customDataArray;
        }
    }

    result[customKey] = resultData;
    return patientData;
}

export async function processFilterForTab({
    filter,
    patientData,
    totalFilterData,
    dynamicCards,
    ninetyDaysData,
    newSavedFilters,
    isCheckNewFilters,
    objectCustom,
    result,
    customTab,
    sixtyDaysData,
    pageType,
    diffDashboardPatients,
    forComparison,
    level,
    isDebugMode = false,
    cleanChainExcludeIds = new Set()
}) {
    const { filters = [] } = filter;

    for (const ele of filters) {
        const { card, isMainCard, items = [] } = ele;
        const key = card?.value;
        if (!key) continue;
        
        if (isMainCard) {
            const customKey = customTab?.accessor;
            const currentCardFilter = isCheckNewFilters && _.includes(newSavedFilters, customKey);

            if (card?.questionId) {
                if (currentCardFilter) {
                    result[customKey] = objectCustom?.[customKey];
                } else {
                    let objectCustomRes = {};
                    const question = ele?.question ?? dynamicCards?.find(({ _id }) => _id === card?.questionId);

                    if (pageType !== filter.dashboard) {
                        const patientIds = new Set(patientData.map(item => item._id));
                        let dashboardWiseData = diffDashboardPatients[filter.dashboard]?.list ?? [];
                        if (level !== 0) {
                            if (level < 0) {
                                dashboardWiseData = dashboardWiseData.filter(item => item.isPastData === true && item.level === level);
                            } else if (level > 0) {
                                dashboardWiseData = dashboardWiseData.filter(item => item.isFutureData === true && item.level === level);
                            }
                        }
                        const referenceMap = new Map(patientData.map(p => [p._id || p.id, p]));
                        const filteredPatients = dashboardWiseData.filter(item => {
                            if (!patientIds.has(item.refPatientId)) return false;
                            if (!matchesSubtype(item, key)) return false;
                            const refEvent = referenceMap.get(item.refPatientId);
                            return isCleanChain(item, refEvent, diffDashboardPatients, filter.dashboard, cleanChainExcludeIds);
                        });

                        await processDynamicCard(question, filteredPatients, objectCustomRes, { ...totalFilterData, isOtherDashboard: true });

                        if (!_.isEmpty(objectCustomRes)) {
                            result[customKey] = objectCustomRes?.[key];
                        }
                    } else {
                        if (level !== 0) {
                            let dashboardWiseData = diffDashboardPatients[filter.dashboard]?.list ?? [];
                            if (level < 0) {
                                dashboardWiseData = dashboardWiseData.filter(item => item.isPastData === true && item.level === level);
                            } else if (level > 0) {
                                dashboardWiseData = dashboardWiseData.filter(item => item.isFutureData === true && item.level === level);
                            }
                            const patientIdsMainSet = new Set(patientData.map(item => item._id));
                            const referenceMap = new Map(patientData.map(p => [p._id || p.id, p]));
                            const filteredPatients = dashboardWiseData
                                .filter(item => item.level === level && patientIdsMainSet.has(item.refPatientId))
                                .filter(item => {
                                    if (!matchesSubtype(item, key)) return false;
                                    const refEvent = referenceMap.get(item.refPatientId);
                                    return isCleanChain(item, refEvent, diffDashboardPatients, filter.dashboard, cleanChainExcludeIds);
                                });
                            await processDynamicCard(
                                question,
                                filteredPatients,
                                objectCustomRes, {
                                ...totalFilterData,
                                level,
                                isOtherDashboard: true
                            }
                            );
                            if (!_.isEmpty(objectCustomRes)) {
                                result[customKey] = objectCustomRes?.[key];
                            }
                        } else {
                            await processDynamicCard(question, patientData, objectCustomRes, { ...totalFilterData, level });
                            if (!_.isEmpty(objectCustomRes)) {
                                result[customKey] = objectCustomRes?.[key];
                            }
                        }
                    }
                }
            } else {
                if (currentCardFilter) {
                    result[customKey] = objectCustom?.[customKey];
                } else {
                    if (pageType === filter.dashboard) {
                        if (level !== 0) {
                            let dashboardWiseData = diffDashboardPatients[filter.dashboard]?.list ?? [];
                            if (level < 0) {
                                dashboardWiseData = dashboardWiseData.filter(item => item.isPastData === true);
                            } else if (level > 0) {
                                dashboardWiseData = dashboardWiseData.filter(item => item.isFutureData === true);
                            }
                            const patientIdsMainSet = new Set(patientData.map(item => item._id));

                            const referenceMap = new Map(patientData.map(p => [p._id || p.id, p]));
                            const filteredPatients = dashboardWiseData
                                .filter(item => item.level === level && patientIdsMainSet.has(item.refPatientId))
                                .filter(item => {
                                    if (!matchesSubtype(item, key)) return false;
                                    const refEvent = referenceMap.get(item.refPatientId);
                                    return isCleanChain(item, refEvent, diffDashboardPatients, filter.dashboard, cleanChainExcludeIds);
                                });

                            result[customKey] = await handleMainCard(
                                key,
                                filteredPatients,
                                {
                                    ...totalFilterData,
                                    level: level,
                                    isOtherDashboard: true,
                                    filterDashboard: filter.dashboard
                                },
                                diffDashboardPatients[filter.dashboard]?.ninetyDaysData ?? [],
                                diffDashboardPatients[filter.dashboard]?.ninetyDaysData?.sixtyDaysData ?? [],
                            );
                        } else {
                            result[customKey] = await handleMainCard(
                                key,
                                patientData,
                                {
                                    ...totalFilterData,
                                    level,
                                    filterDashboard: filter.dashboard
                                },
                                ninetyDaysData,
                                sixtyDaysData
                            );
                        }
                    } else {
                        const patientIds = new Set(patientData.map(item => item._id));
                        const dashboardWiseData = diffDashboardPatients[filter.dashboard]?.list ?? [];
                        let filteredPatients = []
                        const referenceMap = new Map(patientData.map(p => [p._id || p.id, p]));
                        filteredPatients = dashboardWiseData?.filter(item => {
                            let matches = false;
                            if (level < 0) {
                                matches = item.level === level && patientIds.has(item.refPatientId) && item.isPastData === true;
                            } else if (level > 0) {
                                matches = item.level === level && patientIds.has(item.refPatientId) && item.isFutureData === true;
                            } else {
                                matches = item.level === (level + 1);
                            }

                            if (!matches) return false;
                            if (!matchesSubtype(item, key)) return false;
                            const refEvent = referenceMap.get(item.refPatientId);
                            return isCleanChain(item, refEvent, diffDashboardPatients, filter.dashboard, cleanChainExcludeIds);
                        });
                        result[customKey] = await handleMainCard(
                            key,
                            filteredPatients,
                            { ...totalFilterData, isOtherDashboard: true, level: level, filterDashboard: filter.dashboard },
                            diffDashboardPatients[filter.dashboard]?.ninetyDaysData,
                            diffDashboardPatients[filter.dashboard]?.sixtyDaysData
                        );
                    }
                }
            }
        } else {
            if (card?.questionId) {
                const question = ele?.question ?? dynamicCards?.find(({ _id }) => _id === card?.questionId);
                if (!question) continue;
                let objectCustomRes = {};

                if (pageType !== filter.dashboard) {
                    const patientIdsRef = new Set(patientData.map(item => item._id));
                    let dashboardWiseData = diffDashboardPatients[filter.dashboard]?.list ?? [];

                    const referenceMap = new Map(patientData.map(p => [p._id || p.id, p]));
                    let filteredPatients = dashboardWiseData.filter(item => {
                        if (!patientIdsRef.has(item.refPatientId)) return false;
                        if (level !== 0) {
                            const levelMatch = level < 0 ? (item.isPastData === true && item.level === level) : (item.isFutureData === true && item.level === level);
                            if (!levelMatch) return false;
                        }
                        if (!matchesSubtype(item, key)) return false;
                        const refEvent = referenceMap.get(item.refPatientId);
                        return isCleanChain(item, refEvent, diffDashboardPatients, filter.dashboard, cleanChainExcludeIds);
                    });

                    await processDynamicCard(question, filteredPatients, objectCustomRes, { ...totalFilterData });

                    const resultItems = objectCustomRes?.[key] || [];

                    const patientIds = resultItems.reduce((acc, ele) => {
                        if (_.includes(items, ele?._id)) {
                            acc.push(...(ele?.patientIds || []));
                        }
                        return acc;
                    }, []);

                    filteredPatients = filterCustomPatientData(
                        filteredPatients,
                        { [key]: items },
                        {
                            type: key,
                            patientIds,
                            question: { customQuestionInputType: question.customQuestionInputType },
                        },
                        true
                    );
                    const patientIdsRefOther = filteredPatients.map(item => item.refPatientId);
                    patientData = patientData.filter(item => patientIdsRefOther.includes(item._id));
                } else {
                    if (level !== 0) {
                        let dashboardWiseData = diffDashboardPatients[filter.dashboard]?.list ?? [];
                        if (level < 0) {
                            dashboardWiseData = dashboardWiseData.filter(item => item.isPastData === true && item.level === level);
                        } else if (level > 0) {
                            dashboardWiseData = dashboardWiseData.filter(item => item.isFutureData === true && item.level === level);
                        }
                        const patientIdsMainSet = new Set(patientData.map(item => item._id));
                        const referenceMap = new Map(patientData.map(p => [p._id || p.id, p]));
                        dashboardWiseData = dashboardWiseData
                            .filter(item => item.level === level && patientIdsMainSet.has(item.refPatientId))
                            .filter(item => {
                                if (!matchesSubtype(item, key)) return false;
                                const refEvent = referenceMap.get(item.refPatientId);
                                return isCleanChain(item, refEvent, diffDashboardPatients, filter.dashboard, cleanChainExcludeIds);
                            });


                        await processDynamicCard(question, dashboardWiseData, objectCustomRes, { ...totalFilterData, level, isOtherDashboard: true });

                        const resultItems = objectCustomRes?.[key] || [];

                        const patientIds = resultItems.reduce((acc, ele) => {
                            if (_.includes(items, ele?._id)) {
                                acc.push(...(ele?.patientIds || []));
                            }
                            return acc;
                        }, []);

                        dashboardWiseData = await filterCustomPatientData(
                            dashboardWiseData,
                            { [key]: items },
                            {
                                type: key,
                                patientIds,
                                question: { customQuestionInputType: question.customQuestionInputType },
                            },
                            true
                        );
                        const patientIdsRefOther = dashboardWiseData.map(item => item.refPatientId);
                        patientData = patientData.filter(item => patientIdsRefOther.includes(item._id));
                    } else {
                        await processDynamicCard(question, patientData, objectCustomRes, { ...totalFilterData });

                        const resultItems = objectCustomRes?.[key] || [];

                        const patientIds = resultItems.reduce((acc, ele) => {
                            if (_.includes(items, ele?._id)) {
                                acc.push(...(ele?.patientIds || []));
                            }
                            return acc;
                        }, []);

                        patientData = await filterCustomPatientData(
                            patientData,
                            { [key]: items },
                            {
                                type: key,
                                patientIds,
                                question: { customQuestionInputType: question.customQuestionInputType },
                            },
                            true
                        );
                    }
                }
            } else {               
                if (pageType === filter.dashboard) {
                    if (level !== 0) {
                        let dashboardWiseData = diffDashboardPatients[filter.dashboard]?.list ?? [];
                        if (level < 0) {
                            dashboardWiseData = dashboardWiseData.filter(item => item.isPastData === true);
                        } else if (level > 0) {
                            dashboardWiseData = dashboardWiseData.filter(item => item.isFutureData === true);
                        }
                        const referenceMap = new Map(patientData.map(p => [p._id || p.id, p]));
                        const levelWiseIds = dashboardWiseData
                            .filter(item => item.level === level)
                            .filter(item => {
                                if (!matchesSubtype(item, key)) return false;
                                const refEvent = referenceMap.get(item.refPatientId);
                                return isCleanChain(item, refEvent, diffDashboardPatients, filter.dashboard, cleanChainExcludeIds);
                            })
                            .map(item => item.refPatientId);
                        const levelWiseIdsSet = new Set(levelWiseIds);
                        patientData = patientData.filter(item => levelWiseIdsSet.has(item.id || item._id));
                    }
                    patientData = await applyFilter(key, items, patientData, ninetyDaysData, sixtyDaysData, forComparison);
                } else {
                    let dashboardWiseData = diffDashboardPatients[filter.dashboard]?.list ?? [];

                    if (level !== 0) {
                        dashboardWiseData = dashboardWiseData?.filter(item => {
                            if (level < 0) {
                                return item.level === level && item.isPastData === true;
                            } else if (level > 0) {
                                return item.level === level && item.isFutureData === true;
                            }
                            return item.level === level - 1;
                        });
                    }                    
                    const patientIdsRef = new Set(patientData.map(item => item._id || item.id));
                    const referenceMap = new Map(patientData.map(p => [p._id || p.id, p]));
                    dashboardWiseData = await applyFilter(key, items, dashboardWiseData, dashboardWiseData?.ninetyDaysData ?? [], dashboardWiseData?.ninetyDaysData?.sixtyDaysData ?? [], forComparison);                    
                    const patientIds = dashboardWiseData
                        .filter(item => {
                            if (!patientIdsRef.has(item.refPatientId)) {                                
                                return false;
                            }
                            if (!matchesSubtype(item, key)) {
                                return false;
                            }
                            const refEvent = referenceMap.get(item.refPatientId);
                            const clean = isCleanChain(item, refEvent, diffDashboardPatients, filter.dashboard, cleanChainExcludeIds);
                            return clean;
                        })
                        .map(item => item.refPatientId);
                    patientData = patientData.filter(item => patientIds.includes(item._id || item.id));
                }
            }
        }
    }

    return patientData;
}

export async function getDashboardWiseData(diffDashboardPatients, pageType) {

    const typeMapping = {
        [PAGE_TYPE.ADMISSION]: {
            types: [ADT_TYPES.ADMISSIONS, ADT_TYPES.READMISSIONS]
        },
        [PAGE_TYPE.COMMUNITY_TRANSFER]: {
            type: ADT_TYPES.TRANSFER,
            subTypes: [
                ADT_SUB_TYPES.SAFE_DISCHARGE,
                ADT_SUB_TYPES.SNF,
                ADT_SUB_TYPES.AMA
            ]
        },
        [PAGE_TYPE.DECEASED]: {
            type: ADT_TYPES.TRANSFER,
            subTypes: [ADT_SUB_TYPES.DECEASED]
        },
        [PAGE_TYPE.HOSPITAL]: {
            type: ADT_TYPES.TRANSFER,
            subTypes: [
                ADT_SUB_TYPES.PLANNED_HOSPITAL_TRANSFER,
                ADT_SUB_TYPES.UNPLANNED_HOSPITAL_TRANSFER
            ]
        }
    };

    const config = typeMapping[pageType];
    if (!config) return [];

    return diffDashboardPatients.filter(patient => {
        if (config.types) {
            return config.types.includes(patient.type);
        } else if (config.type && config.subTypes) {
            return patient.type === config.type && config.subTypes.includes(patient.transferType);
        }
        return false;
    });
}

export async function getCustomTabsCards(
    patientFilterData,
    customTabs,
    totalFilterData,
    {
        ninetyDaysData,
        dynamicCards,
        newSavedFilters,
        isCheckNewFilters = false,
        objectCustom,
        sixtyDaysData,
        diffDashboardPatients = {},
        pageType,
        forComparison,
        customCombineTabData = [],
        exhustListData = []
    }) {

    const result = {};
    for (const customTab of customTabs) {
        let patientData = [...patientFilterData];

        if (customTab?.type === CUSTOM_TAB_TYPE.combineTab) {
            patientData = await processFilterForCombineTab({
                customTab,
                patientData,
                totalFilterData,
                customCombineTabData,
                pageType,
                ninetyDaysData,
                sixtyDaysData,
                isCheckNewFilters,
                newSavedFilters,
                result,
                forComparison,
                exhustListData,
                objectCustom
            });

        } else {
            let index = 0;
            const filterLevels = customTab.filters.map((_, filterIndex) =>
                calculateCustomTabFilterLevel(filterIndex, customTab, pageType)
            );

            for (const filter of customTab.filters) {
                const calculatedLevel = filterLevels[index];

                const customKey = customTab?.accessor;
                const cleanChainExcludeIds = getCleanChainExcludeIds(
                    diffDashboardPatients,
                    customTab,
                    index,
                    filterLevels
                );
                const currentCardFilter = isCheckNewFilters && _.includes(newSavedFilters, customKey);
                if (!currentCardFilter) {
                    patientData = await processFilterForTab({
                        level: calculatedLevel,
                        customTab,
                        filter,
                        patientData,
                        totalFilterData,
                        dynamicCards,
                        ninetyDaysData,
                        newSavedFilters,
                        isCheckNewFilters,
                        objectCustom,
                        result,
                        sixtyDaysData,
                        pageType,
                        diffDashboardPatients,
                        forComparison,
                        isDebugMode: customKey === "hospitalAndPrior2CommunityCustomtab",
                        cleanChainExcludeIds,
                    });
                }
                index++;
            }
        }
    }
    return result;
}

const mainCardHandlers = {
    [HOSPITAL_CARDS_TYPE.INSURANCE_DATA]: insuranceData,
    [HOSPITAL_CARDS_TYPE.FLOORS_DATA]: floorsData,
    [HOSPITAL_CARDS_TYPE.DCER_DATA]: dcErData,
    [HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS]: hospitalizationData,
    [HOSPITAL_CARDS_TYPE.RETURNS_DATA]: returnsData,
    [HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA]: ninetyDaysDataList,
    [CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA]: sixtyDaysDataList,
    [HOSPITAL_CARDS_TYPE.DAYS_DATA]: daysData,
    [HOSPITAL_CARDS_TYPE.DX_DATA]: dxData,
    [HOSPITAL_CARDS_TYPE.NURSE_DATA]: nurseData,
    [HOSPITAL_CARDS_TYPE.DOCTOR_DATA]: doctorData,
    [HOSPITAL_CARDS_TYPE.SHIFT_DATA]: shiftData,
    [HOSPITAL_CARDS_TYPE.HOSPITAL_DATA]: customHospitalData,
    [CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE_ASS_LIV_DATA]: safeDischargeAssLivData,
    [CO_TRANSFER_CARDS_TYPE.SNF_FACILITY_DATA]: snfFacilityData,

};

function buildSimpleCard(key, patientData, totalFilterData) {
    let filteredPatients = patientData;
    const { filterDashboard } = totalFilterData;

    if (key !== HOSPITAL_CARDS_TYPE.TOTAL) {
        if ([ADMISSION_CARDS_TYPE.ADMISSION, ADMISSION_CARDS_TYPE.READMISSION].includes(key)) {
            filteredPatients = patientData.filter(item => item.type === key);
        } else {
            filteredPatients = patientData.filter(item => item.type === "transfer" && item.transferType === key);
        }
    }

    const total = filteredPatients.length;
    const patientIds = filteredPatients.map(item => totalFilterData.isOtherDashboard ? item.refPatientId : item._id);
    let label = key;
    if (filterDashboard === PAGE_TYPE.COMMUNITY_TRANSFER) {
        label = COMMUNITY_CARD_LABELS?.[key];
    } else if (filterDashboard === PAGE_TYPE.HOSPITAL) {
        label = HOSPITAL_CARDS_LABELS?.[key];
    } else if (filterDashboard === PAGE_TYPE.DECEASED) {
        label = DECEASED_CARDS_LABELS?.[key];
    } else {
        label = ADMISSION_CARDS_LABELS?.[key];
    }
    return [{
        _id: key,
        id: key,
        label,
        name: key,
        total,
        value: total,
        originalTotal: total,
        percentage: itemPercentage(total, totalFilterData.totalForPercentage, "number"),
        patientIds,
        level: totalFilterData?.level,
        ...(totalFilterData.isOtherDashboard && { otherDashboardIds: filteredPatients.map(item => item._id) })
    }];
}

export async function handleMainCard(key, patientData, totalFilterData, ninetyDaysData, sixtyDaysData) {
    if (isSimpleCardKey(key)) {
        return buildSimpleCard(key, patientData, totalFilterData);
    }

    const handler = await mainCardHandlers[key];

    if (!handler) return null;
    if (key === HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA) {
        return await handler(ninetyDaysData, patientData, totalFilterData);
    } else if (key === CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA) {
        return await handler(sixtyDaysData, patientData, totalFilterData);
    } else {
        if (CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE_ASS_LIV_DATA === key) {
            return await handler(patientData, { ...totalFilterData, isSpacialItem: true });
        } else {
            return await handler(patientData, totalFilterData);
        }
    }
}

const directMatchFilters = {
    [HOSPITAL_CARDS_TYPE.INSURANCE_DATA]: 'insuranceId',
    [HOSPITAL_CARDS_TYPE.FLOORS_DATA]: 'floorId',
    [HOSPITAL_CARDS_TYPE.DOCTOR_DATA]: 'doctorId',
    [HOSPITAL_CARDS_TYPE.SHIFT_DATA]: 'shiftName',
    [HOSPITAL_CARDS_TYPE.NURSE_DATA]: 'nurseId',
    [HOSPITAL_CARDS_TYPE.HOSPITAL_DATA]: 'hospitalId',
    [CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE_ASS_LIV_DATA]: 'assistantLivId',
    [CO_TRANSFER_CARDS_TYPE.SNF_FACILITY_DATA]: 'facilityId',
};

const customSyncFilters = {
    [HOSPITAL_CARDS_TYPE.DCER_DATA]: (items) => ({
        wasAdmitted: DCERDataFilter({ DCERData: items }),
    }),
    [HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS]: (items) => ({
        reHospitalization: hospitalizationsFilter({ hospitalizations: items }),
    }),
    [HOSPITAL_CARDS_TYPE.RETURNS_DATA]: (items) => ({
        reHospitalization: returnsDataFilter({ returnsData: items }),
    }),
};

/**
 * Filter data by custom day ranges using a numeric field (default: countDay)
 * Used for custom date analysis (90-days) when items are replaced by ranges.
 */
function applyCustomDateRangeFilter(data = [], ranges = [], dayKey = "countDay") {
    if (!Array.isArray(data) || data.length === 0) return [];
    if (!Array.isArray(ranges) || ranges.length === 0) return data;

    const normalizedRanges = ranges
        .map(range => ({
            min: Number(range?.minDays ?? 0),
            max: Number(range?.maxDays ?? (range?.minDays ?? 0)),
        }))
        .filter(range => !Number.isNaN(range.min) && !Number.isNaN(range.max));

    if (normalizedRanges.length === 0) return data;

    return data.filter(item => {
        const dayValue = Number(item?.[dayKey]);
        if (Number.isNaN(dayValue)) return false;
        return normalizedRanges.some(range => dayValue >= range.min && dayValue <= range.max);
    });
}

/**
 * Normalize time to minutes (consistent with common.js normalizeTime)
 * Converts various time formats to minutes since midnight
 */
function normalizeTimeToMinutes(time) {
    if (time === undefined || time === null) return NaN;

    // Date object or timestamp
    if (time instanceof Date || typeof time === 'number') {
        const date = time instanceof Date ? time : new Date(time);
        if (isNaN(date.getTime())) return NaN;
        return date.getHours() * 60 + date.getMinutes();
    }

    if (typeof time === 'string') {
        const trimmed = time.trim();

        // ISO or date-like string
        if (trimmed.includes('T') || trimmed.includes('-')) {
            const date = new Date(trimmed);
            if (!isNaN(date.getTime())) {
                return date.getHours() * 60 + date.getMinutes();
            }
        }

        // AM/PM handling
        const ampmMatch = trimmed.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
        if (ampmMatch) {
            let hours = parseInt(ampmMatch[1], 10);
            const minutes = parseInt(ampmMatch[2] ?? '0', 10);
            const meridiem = ampmMatch[3].toUpperCase();
            if (meridiem === 'PM' && hours !== 12) hours += 12;
            if (meridiem === 'AM' && hours === 12) hours = 0;
            return hours * 60 + minutes;
        }

        // 24h "HH:mm" or "HH" format fallback
        const timeStr = trimmed.includes(':') ? trimmed : `${trimmed}:00`;
        const [hoursStr, minutesStr] = timeStr.split(':');
        const hours = Number(hoursStr);
        const minutes = Number(minutesStr ?? 0);
        if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
            return hours * 60 + minutes;
        }
    }

    return NaN;
}

/**
 * Filter data by custom time ranges using a time field (default: transferTime)
 * Used for custom time analysis when items are replaced by time ranges.
 * Handles time ranges that may span midnight (e.g., 22:00 to 02:00)
 * Uses normalizeTimeToMinutes for consistent time calculation (same as common.js)
 */
function applyCustomTimeRangeFilter(data = [], ranges = [], timeKey = "transferTime") {
    if (!Array.isArray(data) || data.length === 0) return [];
    if (!Array.isArray(ranges) || ranges.length === 0) return data;

    // Normalize and validate time ranges to minutes
    const normalizedRanges = ranges
        .map(range => {
            const startTime = range?.startTime;
            const endTime = range?.endTime;

            if (!startTime || !endTime) return null;

            const startMinutes = normalizeTimeToMinutes(startTime);
            const endMinutes = normalizeTimeToMinutes(endTime);

            if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes)) {
                return null;
            }

            return {
                startMinutes,
                endMinutes,
                spansMidnight: endMinutes < startMinutes
            };
        })
        .filter(range => range !== null);

    if (normalizedRanges.length === 0) return data;

    return data.filter(item => {
        const timeValue = item?.[timeKey];
        if (!timeValue) return false;

        const patientTimeMinutes = normalizeTimeToMinutes(timeValue);
        if (Number.isNaN(patientTimeMinutes)) return false;

        // Check if the time falls within any of the ranges
        return normalizedRanges.some(range => {
            if (range.spansMidnight) {
                // Time range spans midnight (e.g., 22:00 to 02:00)
                // Match if: patientTime >= startTime (same day, e.g., 23:00 >= 22:00)
                // OR patientTime <= endTime (next day early hours, e.g., 01:00 <= 02:00)
                return patientTimeMinutes >= range.startMinutes || patientTimeMinutes <= range.endMinutes;
            } else {
                // Normal time range (e.g., 09:00 to 17:00)
                // Match if: startTime <= patientTime < endTime (using < for endTime to match common.js logic)
                return patientTimeMinutes >= range.startMinutes && patientTimeMinutes < range.endMinutes;
            }
        });
    });
}

/**
 * Enhanced filter application with comprehensive error handling and validation
 * @param {string} key - Filter key identifier
 * @param {Array} items - Items to filter by
 * @param {Array} patientData - Patient data to filter
 * @param {Array} ninetyDaysData - Ninety days data for special filters
 * @param {Array} sixtyDaysData - Sixty days data for special filters
 * @param {boolean} forComparison - Whether this is for comparison purposes
 * @returns {Promise<Array>} Filtered patient data
 */
export async function applyFilter(key, items, patientData, ninetyDaysData, sixtyDaysData, forComparison = false) {
    // Input validation
    if (!Array.isArray(patientData)) {
        console.warn('applyFilter: patientData is not an array, returning empty array');
        return [];
    }
    // Early return for empty data
    if (patientData.length === 0) {
        return patientData;
    }


    // Handle simple card keys first
    if (isSimpleCardKey(key)) {
        return handleSimpleCardFilter(key, patientData);
    }

    // Return original data if no items to filter
    if (!items || items.length === 0) {
        return patientData;
    }

    // Handle direct field matching filters
    if (directMatchFilters[key]) {
        return handleDirectMatchFilter(key, items, patientData);
    }

    // Handle custom sync filters (single item only)
    if (customSyncFilters[key] && items.length === 1) {
        return handleCustomSyncFilter(key, items, patientData);
    }

    // Handle special case filters
    return await handleSpecialFilters(key, items, patientData, ninetyDaysData, sixtyDaysData);
}

// Optimized simple card filter with constants
function handleSimpleCardFilter(key, patientData) {
    if (TRANSFER_TYPES.includes(key)) {
        return patientData.filter(item => item.type === "transfer" && item.transferType === key);
    }

    if (key === HOSPITAL_CARDS_TYPE.TOTAL) {
        return patientData;
    }

    if (ADMISSION_TYPES.includes(key)) {
        return patientData.filter(item => item.type === key);
    }

    return patientData;
}

// Extracted direct match filter logic with error handling
function handleDirectMatchFilter(key, items, patientData) {
    const field = directMatchFilters[key];

    if (!field) {
        console.warn(`handleDirectMatchFilter: No field mapping found for key: ${key}`);
        return patientData;
    }

    try {
        return patientData.filter((p) => {
            const fieldValue = p[field];
            return fieldValue !== undefined && fieldValue !== null && _.includes(items, fieldValue);
        });
    } catch (error) {
        console.error(`Error in handleDirectMatchFilter for key ${key}:`, error);
        return patientData;
    }
}

// Extracted custom sync filter logic
function handleCustomSyncFilter(key, items, patientData) {
    try {
        const filter = customSyncFilters[key](items);
        return _.isEmpty(filter) ? patientData : _.filter(patientData, filter) ?? [];
    } catch (error) {
        console.error(`Error in handleCustomSyncFilter for key ${key}:`, error);
        return patientData;
    }
}

// Enhanced special filters with better error handling
async function handleSpecialFilters(key, items, patientData, ninetyDaysData, sixtyDaysData) {
    try {
        switch (key) {
            case HOSPITAL_CARDS_TYPE.DAYS_DATA:
                return patientData.filter(({ day }) => day && _.includes(items, day));

            case HOSPITAL_CARDS_TYPE.DX_DATA:
                return patientData.filter(({ dxIds }) =>
                    Array.isArray(dxIds) && _.intersection(items, dxIds).length > 0
                );

            case CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA:
                if (!Array.isArray(sixtyDaysData)) {
                    console.warn('sixtyDaysData is not an array, returning original data');
                    return patientData;
                }
                return await ninetyDaysDataFilter(items, patientData, sixtyDaysData);

            case HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA:
                if (!Array.isArray(ninetyDaysData)) {
                    console.warn('ninetyDaysData is not an array, returning original data');
                    return patientData;
                }
                return await ninetyDaysDataFilter(items, patientData, ninetyDaysData);

            default:
                return patientData;
        }
    } catch (error) {
        console.error(`Error in handleSpecialFilters for key ${key}:`, error);
        return patientData;
    }
}

export const filterPatientDataByLevel = async ({ responseComparison, response, filters, forComparison, pageType }) => {
    const dashboardType = filters?.question?.dashboardType;
    const customTab = filters?.question?.customTab;
    const questionType = customTab?.type ?? null;

    const level = filters?.question?.level;
    const isAnotherDashboard = filters?.question?.isAnotherDashboard;
    const isCustomTab = filters?.question?.isCustomTab;

    if (!dashboardType || !isCustomTab) {

        return forComparison ? responseComparison : response;
    }
    // Use the appropriate response object based on forComparison flag
    let targetResponse = forComparison ? responseComparison : response;

    const anotherDashboard = targetResponse?.diffDashboardPatients?.[dashboardType];
    const dashboardWiseData = targetResponse?.diffDashboardPatients?.[dashboardType]?.list ?? [];
    if (questionType === CUSTOM_TAB_TYPE.combineTab) {
        // Get exhaustListData from targetResponse if available, similar to processFilterForCombineTab
        const exhaustListData = targetResponse?.exhaustListData?.list ?? targetResponse?.exhaustListData ?? [];

        const combineData = await applyCombineFilters({
            patientData: targetResponse?.data ?? [],
            ninetyDaysData: targetResponse?.ninetyDaysData ?? [],
            customCombineTabData: targetResponse?.customCombineTabData ?? [],
            pageType: pageType,
            customTab: customTab,
            forComparison: forComparison,
            exhustListData: Array.isArray(exhaustListData) ? exhaustListData : []
        });

        if (!forComparison) {
            targetResponse.data = combineData;
        }
    } else {
        if (isAnotherDashboard) {
            // Case 1: From another dashboard
            if (level !== 0) {
                if (level < 0) {
                    targetResponse.data = dashboardWiseData.filter(item => item.level === level && item.isPastData === true);
                } else {
                    targetResponse.data = dashboardWiseData.filter(item => item.level === level && item.isFutureData === true);
                }
                targetResponse.ninetyDaysData = anotherDashboard?.ninetyDaysData ?? [];
                targetResponse.sixtyDaysData = anotherDashboard?.sixtyDaysData ?? [];
            } else {
                if (level < 0) {
                    targetResponse.data = dashboardWiseData.filter(item => item.isPastData === true);
                } else {
                    targetResponse.data = dashboardWiseData.filter(item => item.isFutureData === true);
                }
                targetResponse.ninetyDaysData = anotherDashboard?.ninetyDaysData ?? [];
                targetResponse.sixtyDaysData = anotherDashboard?.sixtyDaysData ?? [];
            }
        } else if (level !== 0) {
            // Case 2: From same dashboard and level > 0
            const patientData = targetResponse?.data ?? [];

            const patientIdsSet = new Set(patientData.map(item => item.id));

            targetResponse.data = dashboardWiseData.filter(item => {
                if (level < 0) {
                    return item.level === level && item.isPastData === true && patientIdsSet.has(item.refPatientId);
                } else if (level > 0) {
                    return item.level === level && item.isFutureData === true && patientIdsSet.has(item.refPatientId);
                } else if (level !== 0) {
                    return item.level === level && patientIdsSet.has(item.refPatientId);
                } else {
                    return patientIdsSet.has(item.refPatientId);
                }
            });
        }

        targetResponse.ninetyDaysData = anotherDashboard?.ninetyDaysData ?? [];
        targetResponse.sixtyDaysData = anotherDashboard?.sixtyDaysData ?? [];
    }
    return targetResponse;
}

export async function applyCombineFilters({
    patientData,
    ninetyDaysData,
    customTab,
    sixtyDaysData,
    pageType,
    customCombineTabData = [],
    forComparison = false,
    totalFilterData = {},
    exhustListData = []
}) {
    try {
        // Input validation
        validateFilterInputs(patientData, customTab);

        const filters = customTab.filters;
        const isCustomDateAnalysis = customTab?.isCustomDateAnalysis ?? false;
        const customDateRanges = customTab?.customDateRanges ?? [];
        const customAnalysisType = customTab?.customAnalysisType ?? "date";

        // Use customCombineTabData?.exhaustListData as fallback if exhustListData is not available
        // Similar to processFilterForCombineTab
        const finalExhustListData = (exhustListData && Array.isArray(exhustListData) && exhustListData.length > 0)
            ? exhustListData
            : (customCombineTabData?.exhaustListData?.list && Array.isArray(customCombineTabData.exhaustListData.list) && customCombineTabData.exhaustListData.list.length > 0
                ? customCombineTabData.exhaustListData.list
                : []);

        // Check if exhaustAverageData card is selected (uses customDateRanges, same as 90-day analysis)
        const hasExhaustAverageCard = filters.some((filter) =>
            filter?.filters?.some(filterItem =>
                filterItem?.card?.value === OVERALL_CARDS_TYPE.EXHUST_AVARAGE_DATA
            )
        );
        // Use customDateRanges for exhaust average when exhaust average card is selected and not in custom date analysis mode
        const exhaustAverageRanges = (hasExhaustAverageCard && !isCustomDateAnalysis && customDateRanges && Array.isArray(customDateRanges) && customDateRanges.length > 0)
            ? customDateRanges.filter(item => item.type !== "average") // Filter out averages, only use ranges
            : [];

        let allCurrentPageData = [];
        let allDashboardData = [];

        // Process filters sequentially with index tracking to handle same dashboard appearing multiple times
        // Similar to processFilterForCombineTab, we need to ensure each filter starts with fresh data
        for (let filterIndex = 0; filterIndex < filters.length; filterIndex++) {
            const filter = filters[filterIndex];
            const filterDashboard = filter?.dashboard;
            const filterFilters = filter?.filters ?? [];

            // Check if this filter contains exhaustInsuranceData or exhaustAverageData card - if so, use exhustListData as base
            const hasExhaustInsuranceCard = filterFilters.some(filterItem =>
                filterItem?.card?.value === OVERALL_CARDS_TYPE.EXHUAST_INSURANCE_DATA
            );
            const hasExhaustAverageCard = filterFilters.some(filterItem =>
                filterItem?.card?.value === OVERALL_CARDS_TYPE.EXHUST_AVARAGE_DATA
            );

            if (filterDashboard === pageType || (pageType === PAGE_TYPE.OVERALL && (filterDashboard === CUSTOM_TAB_PAGE_TYPE.OVERALL_INCOMING || filterDashboard === CUSTOM_TAB_PAGE_TYPE.OVERALL_OUTGOING))) {
                // For currentPage filters: Use finalExhustListData if exhaust cards are present, otherwise use patientData
                // Create a fresh copy for each filter to ensure independence when same dashboard appears multiple times
                const freshPatientData = (hasExhaustInsuranceCard || hasExhaustAverageCard) && Array.isArray(finalExhustListData) && finalExhustListData.length > 0
                    ? [...finalExhustListData]
                    : [...patientData];

                const filteredData = await processCurrentPageFilters(
                    filter.filters,
                    freshPatientData,
                    ninetyDaysData,
                    sixtyDaysData,
                    totalFilterData,
                    forComparison,
                    isCustomDateAnalysis,
                    customDateRanges,
                    customAnalysisType,
                    exhaustAverageRanges,
                    hasExhaustInsuranceCard,
                    hasExhaustAverageCard
                );

                // Collect all currentPage results separately
                if (Array.isArray(filteredData) && filteredData.length > 0) {
                    allCurrentPageData.push(...filteredData);
                }
            } else if (filterDashboard !== pageType) {
                // For otherDashboard filters: Create a fresh copy of dashboard data for each filter
                // This ensures independence when same dashboard appears multiple times
                const dashboardCombineData = customCombineTabData[filterDashboard];
                if (dashboardCombineData) {
                    // Use finalExhustListData if exhaust cards are present, otherwise use dashboard data
                    // Create a fresh copy of the data source for each filter group to ensure independence when same dashboard appears multiple times
                    const freshDashboardData = (hasExhaustInsuranceCard || hasExhaustAverageCard) && Array.isArray(finalExhustListData) && finalExhustListData.length > 0
                        ? {
                            ...dashboardCombineData,
                            list: [...finalExhustListData],
                            ninetyDaysData: Array.isArray(dashboardCombineData.ninetyDaysData) ? [...dashboardCombineData.ninetyDaysData] : [],
                            sixtyDaysData: Array.isArray(dashboardCombineData.sixtyDaysData) ? [...dashboardCombineData.sixtyDaysData] : []
                        }
                        : {
                            ...dashboardCombineData,
                            list: Array.isArray(dashboardCombineData.list) ? [...dashboardCombineData.list] : [],
                            ninetyDaysData: Array.isArray(dashboardCombineData.ninetyDaysData) ? [...dashboardCombineData.ninetyDaysData] : [],
                            sixtyDaysData: Array.isArray(dashboardCombineData.sixtyDaysData) ? [...dashboardCombineData.sixtyDaysData] : []
                        };

                    const filteredData = await processOtherDashboardFilters(
                        filter.filters,
                        freshDashboardData,
                        totalFilterData,
                        forComparison,
                        isCustomDateAnalysis,
                        customDateRanges,
                        customAnalysisType,
                        exhaustAverageRanges,
                        hasExhaustInsuranceCard,
                        hasExhaustAverageCard
                    );

                    if (Array.isArray(filteredData) && filteredData.length > 0) {
                        allDashboardData.push(...filteredData);
                    }
                }
            }
        }

        // Handle edge case: if no currentPage filters and pageType is OVERALL
        if (filters.length > 0 && allCurrentPageData.length === 0 && allDashboardData.length === 0 && pageType === PAGE_TYPE.OVERALL) {
            allCurrentPageData = [];
        }

        // Merge all filtered data (do not deduplicate - combineTab needs all occurrences)
        const res = mergeAndDeduplicateData(allCurrentPageData, allDashboardData);
        return res;
    } catch (error) {
        console.error('Error in applyCombineFilters:', error);
        return Array.isArray(patientData) ? patientData : [];
    }
}

// Helper function to merge and deduplicate data
function mergeAndDeduplicateData(filteredPatientData, allDashboardData) {
    const mergedData = [
        ...(Array.isArray(filteredPatientData) ? filteredPatientData : []),
        ...allDashboardData
    ];

    // Remove duplicates based on ID
    // Do not deduplicate, return all merged data as is
    const uniqueData = mergedData;

    return uniqueData;
}

// Enhanced helper functions with better error handling
async function processCurrentPageFilters(
    filterItems,
    patientData,
    ninetyDaysData,
    sixtyDaysData,
    totalFilterData,
    forComparison,
    isCustomDateAnalysis = false,
    customDateRanges = [],
    customAnalysisType = "date",
    exhaustAverageRanges = [],
    hasExhaustInsuranceCard = false,
    hasExhaustAverageCard = false
) {
    if (!Array.isArray(filterItems) || !Array.isArray(patientData)) {
        return patientData;
    }

    let processedData = [...patientData];

    for (const filterItem of filterItems) {
        try {
            const { card } = filterItem;
            let items = filterItem?.items ?? [];
            if (!card?.value) continue;

            if (card?.questionId && (!isCustomDateAnalysis || (isCustomDateAnalysis && customAnalysisType === "date"))) {
                let objectCustomRes = {};

                const originalQuestion = filterItem?.question;
                const key = card?.value;

                if (!originalQuestion) continue;
                const isCustomDateAnalysisAndDate = isCustomDateAnalysis && customAnalysisType === "date" && Array.isArray(customDateRanges) && customDateRanges.length > 0;
                // For custom date analysis, build a new question object with customQuestionOptions derived from customDateRanges
                const questionForProcessing =
                    isCustomDateAnalysisAndDate
                        ? {
                            ...originalQuestion,
                            customQuestionOptions: customDateRanges.map(range => ({
                                min: String(range?.minDays ?? 0),
                                max: String(range?.maxDays ?? 0),
                                type: "range",
                                title: ""
                            }))
                        }
                        : originalQuestion;

                await processDynamicCard(questionForProcessing, processedData, objectCustomRes, { ...totalFilterData });

                const resultItems = objectCustomRes?.[key] || [];
                if (isCustomDateAnalysisAndDate) {
                    items = resultItems?.map(item => item?._id);
                }
                const patientIds = resultItems.reduce((acc, ele) => {
                    if (_.includes(items, ele?._id)) {
                        acc.push(...(ele?.patientIds || []));
                    }
                    return acc;
                }, []);

                processedData = await filterCustomPatientData(
                    processedData,
                    { [key]: items },
                    {
                        type: key,
                        patientIds,
                        question: { customQuestionInputType: questionForProcessing.customQuestionInputType },
                    },
                    true
                );
            } else {
                // Special case: exhaustInsuranceData card - if it has items, filter by insurance
                // Since we're already using exhustListData as base, we just need to filter by selected items
                if (card?.value === OVERALL_CARDS_TYPE.EXHUAST_INSURANCE_DATA && items && items.length > 0) {
                    // Filter exhaust data by selected insurance items
                    processedData = processedData.filter(patient => {
                        // Check if patient's insurance matches any selected item
                        const payerSourceInsuranceId = patient?.payerSourceInsurance?._id || patient?.payerSourceInsurance;
                        const secondaryInsuranceId = patient?.secondaryInsurance?._id || patient?.secondaryInsurance;
                        return _.includes(items, payerSourceInsuranceId) || _.includes(items, secondaryInsuranceId);
                    });
                } else if (card?.value === OVERALL_CARDS_TYPE.EXHUST_AVARAGE_DATA) {
                    // Special case: exhaustAverageData card - calculate average days between dateOfADT and exhaustionDate per range
                    // exhaustAverageRanges are stored at top level (same as customDateRanges) and filter by count field
                    // Filter patients by dateOfADT -> exhaustionDate difference only when ranges exist
                    let dataForAverage = processedData;
                    if (exhaustAverageRanges && Array.isArray(exhaustAverageRanges) && exhaustAverageRanges.length > 0) {
                        dataForAverage = processedData.filter(patient => {
                            if (!patient?.dateOfADT || !patient?.exhaustionDate) return false;
                            const start = moment(patient.dateOfADT).startOf('day');
                            const end = moment(patient.exhaustionDate).startOf('day');
                            if (!start.isValid() || !end.isValid()) return false;
                            const days = end.diff(start, 'days') + 1;
                            return exhaustAverageRanges.some(range => {
                                const min = Number(range?.minDays ?? 0);
                                const max = Number(range?.maxDays ?? (range?.minDays ?? 0));
                                if (Number.isNaN(min) || Number.isNaN(max)) return false;
                                return days >= min && days <= max;
                            });
                        });
                    }

                    // Use only patients that fall into any of the ranges for further processing
                    processedData = dataForAverage;
                } else if (isCustomDateAnalysis && (card?.value === HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA || card?.value === CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA) && Array.isArray(customDateRanges) && customDateRanges.length > 0) {
                    // Custom Date Analysis: use custom day ranges instead of items for ninetyDaysData
                    processedData = await applyCustomDateRangeFilter(processedData, customDateRanges, "countDay");
                } else if (isCustomDateAnalysis && customAnalysisType === "time" && Array.isArray(customDateRanges) && customDateRanges.length > 0) {
                    let timeField = card?.value === "shiftData" ? "transferTime" : card?.value;
                    processedData = applyCustomTimeRangeFilter(processedData, customDateRanges, timeField);
                } else {
                    processedData = await applyFilter(
                        card?.value,
                        items,
                        processedData,
                        ninetyDaysData,
                        sixtyDaysData,
                        forComparison
                    );
                }
            }
        } catch (error) {
            console.error('Error processing filter item:', error);
            continue; // Continue with next filter item
        }
    }

    return processedData;
}

async function processOtherDashboardFilters(
    filterItems,
    dashboardCombineData,
    totalFilterData,
    forComparison,
    isCustomDateAnalysis = false,
    customDateRanges = [],
    customAnalysisType = "date",
    exhaustAverageRanges = [],
    hasExhaustInsuranceCard = false,
    hasExhaustAverageCard = false
) {
    if (!Array.isArray(filterItems)) {
        return [];
    }

    let dashboardData = dashboardCombineData?.list ?? [];
    if (!Array.isArray(dashboardData)) {
        return [];
    }

    const dashboardNinetyDays = dashboardCombineData?.ninetyDaysData ?? [];
    const dashboardSixtyDays = dashboardCombineData?.sixtyDaysData ?? [];

    for (const filterItem of filterItems) {
        try {
            const { card } = filterItem;
            let items = filterItem?.items ?? [];
            if (!card?.value) continue;

            if (card?.questionId && (!isCustomDateAnalysis || (isCustomDateAnalysis && customAnalysisType === "date"))) {
                let objectCustomRes = {};

                const originalQuestion = filterItem?.question;
                const key = card?.value;

                if (!originalQuestion) continue;
                const isCustomDateAnalysisAndDate = isCustomDateAnalysis && customAnalysisType === "date" && Array.isArray(customDateRanges) && customDateRanges.length > 0;
                // For custom date analysis, build a new question object with customQuestionOptions derived from customDateRanges
                const questionForProcessing =
                    isCustomDateAnalysisAndDate
                        ? {
                            ...originalQuestion,
                            customQuestionOptions: customDateRanges.map(range => ({
                                min: String(range?.minDays ?? 0),
                                max: String(range?.maxDays ?? 0),
                                type: "range",
                                title: ""
                            }))
                        }
                        : originalQuestion;

                await processDynamicCard(questionForProcessing, dashboardData, objectCustomRes, { ...totalFilterData });

                const resultItems = objectCustomRes?.[key] || [];
                if (isCustomDateAnalysisAndDate) {
                    items = resultItems?.map(item => item?._id);
                }
                const patientIds = resultItems.reduce((acc, ele) => {
                    if (_.includes(items, ele?._id)) {
                        acc.push(...(ele?.patientIds || []));
                    }
                    return acc;
                }, []);

                dashboardData = await filterCustomPatientData(
                    dashboardData,
                    { [key]: items },
                    {
                        type: key,
                        patientIds,
                        question: { customQuestionInputType: questionForProcessing.customQuestionInputType },
                    },
                    true
                );
            } else {
                // Special case: exhaustInsuranceData card - if it has items, filter by insurance
                // Since we're already using exhustListData as base, we just need to filter by selected items
                if (card?.value === OVERALL_CARDS_TYPE.EXHUAST_INSURANCE_DATA && items && items.length > 0) {
                    // Filter exhaust data by selected insurance items
                    dashboardData = dashboardData.filter(patient => {
                        // Check if patient's insurance matches any selected item
                        const payerSourceInsuranceId = patient?.payerSourceInsurance?._id || patient?.payerSourceInsurance;
                        const secondaryInsuranceId = patient?.secondaryInsurance?._id || patient?.secondaryInsurance;
                        return _.includes(items, payerSourceInsuranceId) || _.includes(items, secondaryInsuranceId);
                    });
                } else if (card?.value === OVERALL_CARDS_TYPE.EXHUST_AVARAGE_DATA) {
                    // Special case: exhaustAverageData card - calculate average days between dateOfADT and exhaustionDate per range
                    // exhaustAverageRanges are stored at top level (same as customDateRanges) and filter by count field
                    // Filter patients by dateOfADT -> exhaustionDate difference only when ranges exist
                    let dataForAverage = dashboardData;
                    if (exhaustAverageRanges && Array.isArray(exhaustAverageRanges) && exhaustAverageRanges.length > 0) {
                        dataForAverage = dashboardData.filter(patient => {
                            if (!patient?.dateOfADT || !patient?.exhaustionDate) return false;
                            const start = moment(patient.dateOfADT).startOf('day');
                            const end = moment(patient.exhaustionDate).startOf('day');
                            if (!start.isValid() || !end.isValid()) return false;
                            const days = end.diff(start, 'days') + 1;
                            return exhaustAverageRanges.some(range => {
                                const min = Number(range?.minDays ?? 0);
                                const max = Number(range?.maxDays ?? (range?.minDays ?? 0));
                                if (Number.isNaN(min) || Number.isNaN(max)) return false;
                                return days >= min && days <= max;
                            });
                        });
                    }

                    // Use only patients that fall into any of the ranges for further processing
                    dashboardData = dataForAverage;
                } else if (isCustomDateAnalysis && (card?.value === HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA || card?.value === CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA) && Array.isArray(customDateRanges) && customDateRanges.length > 0) {
                    // Custom Date Analysis: use custom day ranges instead of items for ninetyDaysData
                    dashboardData = await applyCustomDateRangeFilter(dashboardData, customDateRanges, "countDay");
                } else if (isCustomDateAnalysis && customAnalysisType === "time" && Array.isArray(customDateRanges) && customDateRanges.length > 0) {
                    let timeField = card?.value === "shiftData" ? "transferTime" : card?.value;
                    dashboardData = applyCustomTimeRangeFilter(dashboardData, customDateRanges, timeField);
                } else {
                    dashboardData = await applyFilter(
                        card?.value,
                        items,
                        dashboardData,
                        dashboardNinetyDays,
                        dashboardSixtyDays,
                        false
                    );
                }
            }
        } catch (error) {
            console.error('Error processing dashboard filter item:', error);
            continue; // Continue with next filter item
        }
    }

    return dashboardData;
}

